/**
 * Reluxe Product Accordion
 * Collapsible sections for Description, Size, and Shipping/Returns
 * Parses multiple description formats and displays them uniformly
 */

(function() {
  'use strict';

  // Lines to skip entirely (useless disclaimers)
  const SKIP_PATTERNS = [
    /^\*please allow/i,
    /^\*measurements are taken/i,
    /^\*if the stated size/i,
    /^\*materials may be listed/i,
    /^\*as this is a used item/i,
    /^\*depending on the item/i,
    /^\*we use.*grid sheet/i,
    /^the actual size may differ/i,
    /^about size$/i,
    /^please read and understand/i,
    /^please note that we sell/i,
    /^the box in the picture/i,
    /^the item is purchased from/i,
    /^all sales are final/i,
    /^most items.*are preloved/i,
    /^preloved items may have odor/i,
    /^all of our items undergo/i,
    /^the color in the photo may be different/i,
    /^please look at photos and read/i,
    /^□+$/,
    /^-+$/,
    /^item name$/i,
    /^item name\s+/i,  // "Item name" followed by product title
    /^measurements are taken/i,
    /^when the size.*is not indicated/i,
    /^actual size may differ/i,
    /^please note that depending/i,
    /^please note that these shoes are pre-owned/i,
    // Clothing disclaimers
    /^since this is clothing/i,
    /^please be aware that there may be slight fraying/i,
    /^although the actual size is shown/i,
    /^there may be slight errors/i,
    /^if you have any questions regarding size/i,
    /^please feel free to contact us/i,
    // Rank-related patterns
    /^\*?please see below for rank details/i,
    /^item rank\s*:\s*[A-Z]/i,
    /^rank\s*:\s*[A-Z]$/i,
    // Other skip patterns
    /^engraving\s*:/i,
    /^engraved\s*:/i,
    /^made in \d{4}/i,
    /^\[engraving\]/i,
    /^[•\-]\s*notes?\s*:\s*$/i,  // Empty notes header
    /^there are no accessories other than/i,  // Accessory disclaimer
    /^detail remarks\s*:/i,  // Detail remarks header
    /^\[?condition\s*rank\]?\.?\s*$/i,  // Empty "Condition Rank" or "[Condition Rank]." header
    // Freeform description fields - hide when using AI descriptions
    /^details?\s*:/i,  // "Details:" or "Detail:" - freeform descriptions
    /^comments?\s*:/i,  // "Comments:" or "Comment:" - seller commentary
    /^about\s+us\s*[-:]/i,  // "About Us:" or "About us -" - store info
    /^description\s*[;:]/i,  // "Description:" or "Description;" - freeform descriptions
    // Generic seller commentary about usability (redundant with AI descriptions)
    /^(but\.?\s*)?(however\.?\s*)?(it\s+)?can\s+still\s+be\s+used/i,
    /^(but|however)\.?\s*(it\s+)?(is\s+)?(still\s+)?(usable|functional|works?)/i,
    /can\s+still\s+be\s+used/i,  // Catch this phrase anywhere in the line
    /^the color may look different depending on/i,  // PC/monitor color disclaimer
    /^the items shown in the images are all that are included/i,  // Images disclaimer
    // Japanese shipping/delivery disclaimers
    /^scheduled delivery/i,
    /^sagawa express/i,
    /^hikyaku courier/i,
    /^\d+\s*size\s*\*?customers/i,  // "140 size *Customers cannot..."
    /^customers cannot specify/i,
    /^we will ship within/i,
    /^ship within \d+ business days/i,
    /^excluding weekends and holidays/i,
    /^after payment/i,
    /^delivery method/i,
    /^delivery company/i,
    /^yamato transport/i,
    /^japan post/i,
    /^kuroneko/i,
    // Rank scale explanations (S, A, B, C, D rating definitions) - EXPANDED
    // S rank variations
    /^S\s*:\s*(almost|nearly|new|unused|mint|the\s+item|item\s+shows|item\s+is)/i,
    // A rank variations
    /^A\s*:\s*(it\s+is|very\s+good|excellent|good\s+condition|in\s+very\s+good|item\s+has|the\s+item|item\s+is)/i,
    // B rank variations
    /^B\s*:\s*(there\s+are|some\s+signs|moderate|used|normal|item\s+with|the\s+item|item\s+has|item\s+is)/i,
    // C rank variations
    /^C\s*:\s*(in\s+addition|noticeable|significant|heavy|well\s+used|item\s+with|the\s+item|item\s+has)/i,
    // D rank variations
    /^D\s*:\s*(poor|damaged|junk|for\s+parts|item\s+is|the\s+item)/i,
    // N rank variations (new)
    /^N\s*:\s*(new|unused|brand\s+new|item\s+is|the\s+item)/i,
    // Generic single-letter rank followed by item condition description
    /^[SABCDN]\s*:\s*(?:the\s+)?item\s+(?:shows?|has|is|with)\s/i,
    /^SA\s*:\s*/i,  // SA rank
    /^AB\s*:\s*/i,  // AB rank
    /^BC\s*:\s*/i,  // BC rank
    /^about\s+condition\s+rank/i,
    /^condition\s+rank\s*:/i,
    /^rank\s+explanation/i,
    /^rank\s+guide/i,
    // Empty or useless headers
    /^remarks?\s*$/i,
    /^remarks?\s*:\s*$/i,
    /^condition\s*-?\s*$/i,
    /^-?\s*condition\s*-?\s*$/i,
    /^reference\s+price\s*:\s*-?\s*$/i,
    /^product\s+name\s*:/i,
    // Marketing fluff patterns
    /^this is a [a-z]+ [a-z]+.*muffler/i,
    /^made of high.quality/i,
    /^be sure to use it/i,
    /^recommended scenes?\s*-?\s*$/i,
    /^recommended scenes?\s*-/i,
    /^the calm .* color goes/i,
    /^due to the universal design/i,
    /^it is perfect for pairing/i,
    /^it has a soft texture/i,
    /^will brighten up your/i,
    /^please check the posted photos/i,
    /^\*?this is a used item/i,
    /^please note that since we are dealing with used items/i,
    /^we are unable to fully grasp/i,
    /^although there are some scratches/i,
    /^this item has little signs of use/i,
    // "Check condition/photos" disclaimers
    /please\s+check\s+(the\s+)?(condition|photos?)/i,
    /there\s+is\s+no\s+other\s+damage/i,
    /the\s+item\s+can\s+be\s+used/i,
    /before\s+considering\s+purchas/i,
    // Color disclaimer variations
    /^-?\s*color\s*-?\s*$/i,
    /^\[gray-?\]\s*$/i,
    // "See photo/image" lines - useless without content
    /^\(?\s*(see\s+)?(photo|image)s?\s*\)?\.?$/i,
    /^refer\s+to\s+(photo|image)/i,
    /^see\s+attached\s+(photo|image)/i,
    /^material\s*:\s*(see\s+)?(photo|image)/i,
    /^color\s*:\s*(see\s+)?(photo|image)/i,
    /^\[.*-\]\s*$/i,  // [Gray-] type patterns
  ];

  // Fields to remove entirely from output
  const FIELDS_TO_REMOVE = [
    'rank', 'item rank', 'engraving', 'engraved',
    'product number', 'management number', 'control number',
    'shape number and item number',  // Model number often has redundant info
    'reference price',  // Useless empty field
    'product name',  // Redundant with title
    'condition rank',  // We don't use ranks
    'remarks',  // Usually empty
    // NOTE: notation size, size indicated, and reference size go to Size section, not removed
  ];

  // Generic/useless condition statements to filter out
  const USELESS_CONDITION_PATTERNS = [
    /^i think it is in good condition overall\.?$/i,
    /^it is in good condition overall\.?$/i,
    /^good condition overall\.?$/i,
    /^the item is in good condition\.?$/i,
    /^overall good condition\.?$/i,
    /^in good condition\.?$/i,
    /^condition\s*:\s*$/i,  // Empty "Condition:"
    /^overall\s+condition$/i,  // Just "Overall Condition" header
    /^condition\s+details?$/i,  // Just "Condition details" header
    /^the item has been stored without being used/i,  // Storage disclaimer
    /^the condition of the item may slightly deteriorate/i,  // Storage deterioration disclaimer
    /^please note\s*[•\*]?\s*$/i,  // "Please note •" or "Please note *" header
    /^[•\*]?\s*please note\s*\*?\s*about included information/i,  // "Please note *About included information..."
    /^\*?about included information/i,  // "*About included information:"
    /^items not listed or shown in the photo are not included/i,  // Included items disclaimer
    /^also\.?\s*there may be scratches or dirt/i,  // "Also. there may be scratches or dirt..."
    /there may be scratches or dirt that are not listed/i,  // Generic scratches disclaimer (anywhere in line)
    /^in addition\.?\s*there may be scratches or dirt/i,  // Additional scratches disclaimer
    /^\([A-Z]\s*stamp\)$/i,  // "(O stamp)" or similar stamp markers
    // Rank scale explanations (various formats) - EXPANDED to catch more variations
    // S rank variations
    /^S\s*:\s*(almost|nearly|new|unused|mint|the\s+item|item\s+shows|item\s+is)/i,
    // A rank variations
    /^A\s*:\s*(it\s+is|very\s+good|excellent|good\s+condition|in\s+very\s+good|item\s+has|the\s+item|item\s+is)/i,
    // B rank variations
    /^B\s*:\s*(there\s+are|some\s+signs|moderate|used|normal|item\s+with|the\s+item|item\s+has|item\s+is)/i,
    // C rank variations
    /^C\s*:\s*(in\s+addition|noticeable|significant|heavy|well\s+used|item\s+with|the\s+item|item\s+has)/i,
    // D rank variations
    /^D\s*:\s*(poor|damaged|junk|for\s+parts|item\s+is|the\s+item)/i,
    // N rank variations (new)
    /^N\s*:\s*(new|unused|brand\s+new|item\s+is|the\s+item)/i,
    // Generic single-letter rank followed by item condition description
    /^[SABCDN]\s*:\s*(?:the\s+)?item\s+(?:shows?|has|is|with)\s/i,
    /^SA\s*:?\s*/i,
    /^AB\s*:?\s*/i,
    /^BC\s*:?\s*/i,
    /^about\s+condition\s+rank/i,
    /^condition\s+rank\s+[A-Z]$/i,
    /^rank\s+[A-Z]\s*$/i,
    // Extended rank scale patterns (run-on format like "SUnused item" or "AExcellent Condition")
    /^\[?Rank\s*S/i,  // "[Rank SUnused item..."
    /^S\s*Unused\s+item/i,
    /^SA\s*A\s+beautiful\s+USED/i,
    /^A\s*Excellent\s+Condition/i,
    /^AB\s*Good\s+item/i,
    /^B\s*Used\s+item\s+with\s+signs/i,
    /^BC\s*USED\s+product/i,
    /^C\s*Difficult/i,
    /beautiful\s+USED\s+item\s+in\s+very\s+good\s+condition/i,
    /beautiful\s+USED\s+item\s+that\s+has\s+been\s+used\s+less/i,
    /Relatively\s+good\s+item\s+There\s+is\s+some\s+signs/i,
    /more\s+noticeable\s+usability\s+than\s+rank/i,
    /Junk\s*item\s*USED\s+item\s+that\s+is\s+difficult/i,
    /according\s+to\s+our\s+inspection\s+standards/i,
    // Bullet-style headers that don't belong in condition
    /^●\s*condition\s*$/i,
    /^●\s*color\s*$/i,
    /^●\s*material\s*$/i,
    /^●\s*comment\s*$/i,
    /^●\s*item\s*number\s*$/i,
    /^●\s*/i,  // Any bullet header
    // Marketing/sales phrases
    /^if you like it\.?\s*please take this opportunity/i,
    /^please take this opportunity/i,
    // Item number lines
    /^\d{5,}$/,  // Just a long number (item number)
    // Empty headers
    /^remarks?\s*$/i,
    /^condition\s+description\s*$/i,
    /^material\s+see\s+image/i,
    /^color\s*-?\s*$/i,
    /^\[gray-?\]/i,
    /^\[.*-\]\s*$/i,
    // Outer/Lining material lines (should be in Description, not Condition)
    /^outer$/i,
    /^lining\s*:/i,
    /^outer\s*:/i,
    // "No notable damage" and similar non-informative entries
    /no\s+notable\s+damage/i,
    /no\s+particular\s+damage/i,
    /no\s+significant\s+damage/i,
    /no\s+major\s+damage/i,
    /no\s+visible\s+damage/i,
    /no\s+damage/i,
    /no\s+flaws/i,
    /no\s+defects/i,
    /no\s+issues/i,
    /nothing\s+notable/i,
    /none\s+in\s+particular/i,
    /^-?\s*none\.?\s*$/i,
    /^no\s+notes?\.?\s*$/i,
    // Empty section headers
    /^special\s+notes?\s*:?\s*$/i,
    /^serial\s+number\s*-?\s*$/i,
    /^serial\s*:/i,  // Any serial entry should be hidden
    /^\[?\s*serial\s*(number)?\s*\]?/i,  // [Serial], [Serial number], Serial, etc.
    /^condition\s*$/i,
    /^\[outside[^\]]*\]\s*$/i,
    /^\[inside[^\]]*\]\s*$/i,
    /^inside[^:]*:\s*$/i,
    /^outside[^:]*:\s*$/i,
    /^-?\s*overall\s*:?\s*$/i,
    // Generic disclaimers - ONLY filter if the ENTIRE entry is just these useless phrases
    /^\*?\s*please\s+note\s+that\s+there\s+may\s+be\s+slight\s+differences?\.?\s*$/i,
    /^there\s+may\s+be\s+slight\s+differences?\.?\s*$/i,
    /^but\s+overall\s+there\s+is\s+no\s+noticeable\s+damage[^.]*\.?\s*$/i,
    /^you\s+can\s+still\s+use\s+it\.?\s*$/i,
    // "See photo/image" entries - useless
    /^\(?\s*(see\s+)?(photo|image)s?\s*\)?\.?$/i,
    /^refer\s+to\s+(photo|image)/i,
    /^see\s+attached\s+(photo|image)/i,
    // Marketing/product description text that got into condition
    /has arrived from/i,
    /this is a cute/i,
    /this is a beautiful/i,
    /this is a lovely/i,
    /this is a great/i,
    /this is a nice/i,
    /cute border pattern/i,
    /you can still wear it/i,
    /you can still use it/i,
    /without any problems/i,
    /so it is rated/i,
    /rated [A-Z]\./i,
    // Used item disclaimers
    /since this is a used item/i,
    /there may be damage that is not listed/i,
    /thank you for your understanding/i,
    // "Check photos/condition" disclaimers
    /please\s+check\s+(the\s+)?(condition|photos?)/i,
    /check\s+(the\s+)?(condition|photos?)\s+(with\s+photos?|before)/i,
    /there\s+is\s+no\s+other\s+damage/i,
    /the\s+item\s+can\s+be\s+used/i,
    /before\s+considering\s+purchas/i,
    /considering\s+purchas/i,
    // Product descriptions that got into condition
    /^[A-Za-z]+'s\s+(women'?s?|men'?s?)\s+accessories/i,  // "Chanel's women's accessories..."
    /^an\s+item\s+designed\s+in/i,  // "An item designed in the shape of..."
    /in\s+good\s+condition\s+with\s+no\s+noticeable\s+damage/i,  // Generic useless statement
    /the\s+item\s+is\s+in\s+good\s+condition/i,
    /with\s+no\s+noticeable\s+damage\s+during\s+use/i,
    /^``[^']+''\.?$/i,  // Just quoted product names like "``Coco Mark Earrings''."
    /coco\s+mark.*motif/i,  // Marketing description
    /iconic.*motif/i,  // Marketing description
    // General used item disclaimers
    /^\*?overall\.?\s*there\s+are\s+signs\s+of\s+use/i,  // "*Overall. There are signs of use..."
    /there\s+are\s+signs\s+of\s+use\s+and\s+damage\s+as\s+a\s+used\s+item/i,
    /does\s+not\s+interfere\s+with\s+normal\s+use/i,
    /check\s+the\s+posted\s+photos?\s+and\s+contents?/i,
    /understand\s+them\s+before\s+purchasing/i,
    /this\s+is\s+a\s+used\s+item\.?\s*so\s+please\s+check/i,  // "*This is a used item. So please check..."
    /please\s+check\s+the\s+photos?\s+and\s+descriptions?/i,
    /^\*?this\s+is\s+a\s+used\s+item/i,  // Stand-alone used item disclaimer
  ];

  // Measurement keywords
  const MEASUREMENT_KEYWORDS = [
    'width', 'height', 'depth', 'length', 'vertical', 'shoulder', 'bust', 'waist',
    'inseam', 'rise', 'hem', 'chest', 'sleeve', 'handle', 'strap', 'outsole', 'insole',
    'shell width', 'heel height', 'platform', 'maximum opening', 'opening width',
    'shoulder drop', 'handle drop', 'skirt length', 'side slit', 'drop', 'instep width',
    'instep', 'heel', 'total length', 'number of holes', 'buckle',
    'lens width', 'lens height', 'frame', 'temple', 'bridge',
    'base length', 'base width', 'belt size', 'belt length'
  ];

  // Shipping & Returns content (static)
  const SHIPPING_RETURNS_CONTENT = `
    <div class="reluxe-accordion__shipping">
      <p class="reluxe-accordion__shipping-region"><strong>US Domestic</strong></p>
      <ul>
        <li><strong>Standard (2-7 business days):</strong> FREE on orders over $500, otherwise $25</li>
        <li><strong>Overnight:</strong> $85 (next business day after dispatch)</li>
      </ul>
      <p class="reluxe-accordion__shipping-region"><strong>Canada</strong></p>
      <ul>
        <li><strong>Standard (3-8 business days):</strong> FREE on orders over $500, otherwise $35</li>
      </ul>
      <p class="reluxe-accordion__shipping-region"><strong>International</strong></p>
      <ul>
        <li><strong>Standard (3-8 business days):</strong> $70 flat rate</li>
        <li>Custom duties and taxes may apply at delivery</li>
        <li>Exotic materials cannot be shipped outside the US</li>
      </ul>
      <p style="margin-top: 12px; font-size: 13px; color: #666;">Handling time: 2-5 business days. All shipments include insurance, signature confirmation, and quality packaging.</p>
      <p class="reluxe-accordion__shipping-region"><strong>Returns</strong></p>
      <p>Please review our <a href="https://thereluxe.com/policies/refund-policy">Return Policy</a> for full details on returns and exchanges.</p>
    </div>
  `;

  /**
   * Remove non-description elements from HTML string
   */
  function separateNonDescriptionElements(html) {
    const temp = document.createElement('div');
    temp.innerHTML = html;

    const selectorsToExtract = [
      '.product-inquiry-wrapper',
      '.social-sharing',
      '[class*="product-inquiry"]',
      '[class*="social-sharing"]',
      '[class*="share-buttons"]'
    ];

    const extractedElements = [];
    for (const selector of selectorsToExtract) {
      const elements = temp.querySelectorAll(selector);
      elements.forEach(el => {
        extractedElements.push(el.outerHTML);
        el.remove();
      });
    }

    return { cleanHTML: temp.innerHTML, extractedElements };
  }

  /**
   * Clean display value for info fields (Color, Material, Model, Type, Accessories, etc.)
   * - Strips "as shown in image/photo", "shown in image", "see photo" type phrases
   * - Strips trailing "and others", "and other", "etc.", "etc"
   * - Returns null if value is empty after cleaning (so the line can be hidden)
   */
  function cleanDisplayValue(value) {
    if (!value || value === '-') return null;

    let cleaned = value.trim();

    // Hide lines that are ONLY disclaimers/section headers with no useful info
    if (/^[\(\[]?\s*please\s+allow\s+for\s+slight\s+variation/i.test(cleaned)) return null;
    if (/^\s*damage\s+details?\s*[:\.]?\s*$/i.test(cleaned)) return null;

    // Strip "as shown in image/photo/s" and variants (anywhere in string, with optional parentheses)
    cleaned = cleaned.replace(/\s*\(?\s*as\s+shown\s+(in\s+)?(the\s+)?(item\s+)?(photo|image)s?\s*\)?\s*/gi, ' ');
    cleaned = cleaned.replace(/\s*\(?\s*shown\s+in\s+(the\s+)?(item\s+)?(photo|image)s?\s*\)?\s*/gi, ' ');
    cleaned = cleaned.replace(/\s*\(?\s*see\s+(the\s+)?(attached\s+)?(photo|image)s?\s*\)?\s*/gi, ' ');
    cleaned = cleaned.replace(/\s*\(?\s*refer\s+to\s+(the\s+)?(photo|image)s?\s*\)?\s*/gi, ' ');
    cleaned = cleaned.replace(/\s*\(?\s*(photo|image)s?\s*\)?\s*$/gi, ' ');

    // Strip "please allow for slight variations" disclaimer when mixed with other content
    cleaned = cleaned.replace(/\s*[\(\[]?\s*please\s+allow\s+for\s+slight\s+variations?\s*(in\s+measurements?)?\s*[\)\]]?\s*/gi, ' ');

    // Strip trailing "and others", "and other", "etc.", "etc" (with optional comma/space before)
    cleaned = cleaned.replace(/\s*[,;]?\s*(and\s+others?|etc\.?)\s*$/gi, '');

    // Clean up multiple spaces and trim
    cleaned = cleaned.replace(/\s{2,}/g, ' ').trim();

    // Return null if empty after cleaning
    if (!cleaned || cleaned === '-') return null;

    return cleaned;
  }

  /**
   * Check if a line looks like a measurement
   */
  function isMeasurementLine(line) {
    const lower = line.toLowerCase();

    // EXCLUDE long paragraph descriptions - they may contain measurements but are descriptions
    // These typically start with "This is" and are narrative text, not measurement data
    if (line.length > 100 && /^this is/i.test(line)) {
      return false;
    }

    // EXCLUDE Accessories lines - they may contain "shoulder strap" but are not measurements
    if (/^accessories\s*:/i.test(line)) {
      return false;
    }

    // EXCLUDE product titles - they contain "SIZE" but aren't measurements
    // Product titles typically have brand names and "SIZE XX" format
    if (/\bSIZE\s+\d/i.test(line) && /\b(gucci|louis vuitton|chanel|hermes|prada|dior|fendi|balenciaga|burberry|celine|bottega|loewe|valentino|givenchy|ysl|saint laurent)\b/i.test(line)) {
      return false;
    }

    // EXCLUDE lines that look like product titles (brand + SIZE + measurement)
    if (/^[A-Za-z].*\bSIZE\s+\d+.*\d+cm/i.test(line)) {
      return false;
    }

    // Check for measurement keywords followed by numbers
    for (const kw of MEASUREMENT_KEYWORDS) {
      if (lower.includes(kw) && /\d/.test(line)) {
        return true;
      }
    }
    // Check for dimension format: "30cm" or "12 x 9 x 2" but NOT product titles
    if (/\d+(?:\.\d+)?\s*(?:cm|in|"|'|mm)/i.test(line)) {
      // Must start with a measurement keyword or be a simple measurement
      if (/^(outsole|insole|heel|shell|width|height|depth|length|vertical|shoulder|bust|waist|inseam|rise|hem|chest|sleeve|handle|strap|platform|drop|maximum|opening)/i.test(line)) {
        return true;
      }
      // Or be in "Key: value" format where key is short
      if (/^[a-z\s]{1,25}:\s*\d/i.test(line)) {
        return true;
      }
      // Or be just dimensions like "30cm x 26cm"
      if (/^\d+(?:\.\d+)?\s*(?:cm|in|mm)\s*(?:x|\()/i.test(line)) {
        return true;
      }
      // Or be W/H/D abbreviation format like "W20cm x H15cm x D11cm"
      if (/^[WHD]\s*\d+(?:\.\d+)?\s*(?:cm|in|mm)/i.test(line)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Check if line should be skipped
   */
  function shouldSkipLine(line) {
    const trimmed = line.trim();

    // Check skip patterns
    if (SKIP_PATTERNS.some(p => p.test(trimmed))) return true;

    // Check if this is a field we want to remove entirely
    const kvMatch = trimmed.match(/^([^:]+):\s*/i);
    if (kvMatch) {
      const key = kvMatch[1].toLowerCase().trim();
      if (FIELDS_TO_REMOVE.includes(key)) return true;
    }

    return false;
  }

  /**
   * Check if condition text is useless/generic
   */
  function isUselessCondition(text) {
    const trimmed = text.trim();
    return USELESS_CONDITION_PATTERNS.some(p => p.test(trimmed));
  }

  /**
   * Clean condition text - remove redundant prefixes and useless phrases
   */
  function cleanConditionText(text) {
    let cleaned = text.trim();

    // Remove leading "Condition:" prefix (redundant since we're already in condition section)
    cleaned = cleaned.replace(/^condition\s*:\s*/i, '');

    // Remove leading bullet/dash
    cleaned = cleaned.replace(/^[•\-]\s*/, '');

    // Remove "Overall Condition" prefix
    cleaned = cleaned.replace(/^overall\s+condition\s*/i, '');

    // Remove "Condition details" at the end
    cleaned = cleaned.replace(/\s*condition\s+details?\s*$/i, '');

    // Remove storage disclaimers
    cleaned = cleaned.replace(/\s*the item has been stored without being used\.?\s*/gi, '');
    cleaned = cleaned.replace(/\s*the condition of the item may slightly deteriorate due to storage\.?\s*/gi, '');

    // Remove filler phrases (strip from within entries, keep the rest)
    cleaned = cleaned.replace(/\s*but\s+overall\s+there\s+is\s+no\s+noticeable\s+damage[^.]*\.?\s*/gi, '');
    cleaned = cleaned.replace(/\s*overall\s+there\s+is\s+no\s+noticeable\s+damage[^.]*\.?\s*/gi, '');
    cleaned = cleaned.replace(/\s*and\s+you\s+can\s+still\s+use\s+it\.?\s*/gi, '');
    cleaned = cleaned.replace(/\s*you\s+can\s+still\s+use\s+it\.?\s*/gi, '');
    cleaned = cleaned.replace(/\s*\*?\s*please\s+note\s+that\s+there\s+may\s+be\s+slight\s+differences?\.?\s*/gi, '');
    cleaned = cleaned.replace(/\s*there\s+may\s+be\s+slight\s+differences?\.?\s*/gi, '');
    // Remove general used item disclaimers (inline)
    cleaned = cleaned.replace(/\s*\*?overall\.?\s*there\s+are\s+signs\s+of\s+use\s+and\s+damage\s+as\s+a\s+used\s+item\.?\s*/gi, '');
    cleaned = cleaned.replace(/\s*but\s+this\s+item\s+does\s+not\s+interfere\s+with\s+normal\s+use\.?\s*/gi, '');
    cleaned = cleaned.replace(/\s*this\s+item\s+does\s+not\s+interfere\s+with\s+normal\s+use\.?\s*/gi, '');
    cleaned = cleaned.replace(/\s*please\s+check\s+the\s+posted\s+photos?\s+and\s+contents?\s+and\s+understand\s+them\s+before\s+purchasing\.?\s*/gi, '');
    cleaned = cleaned.replace(/\s*\*?this\s+is\s+a\s+used\s+item\.?\s*so\s+please\s+check\s+the\s+photos?\s+and\s+descriptions?\s+and\s+understand\s+them\s+before\s+purchasing\.?\s*/gi, '');
    cleaned = cleaned.replace(/\s*please\s+check\s+the\s+photos?\s+and\s+descriptions?\s+and\s+understand\s+them\s+before\s+purchasing\.?\s*/gi, '');
    // Remove "Please note *About included information" disclaimers
    cleaned = cleaned.replace(/\s*[•\*]?\s*please\s+note\s*\*?\s*about\s+included\s+information[^.]*\.?\s*/gi, '');
    cleaned = cleaned.replace(/\s*items\s+not\s+listed\s+or\s+shown\s+in\s+the\s+photo\s+are\s+not\s+included\.?\s*/gi, '');
    // Remove "Also. there may be scratches" disclaimers
    cleaned = cleaned.replace(/\s*also\.?\s*there\s+may\s+be\s+scratches\s+or\s+dirt\s+that\s+are\s+not\s+listed\.?\s*/gi, '');
    cleaned = cleaned.replace(/\s*there\s+may\s+be\s+scratches\s+or\s+dirt\s+that\s+are\s+not\s+listed\.?\s*/gi, '');

    // Clean up location headers like "[Outside of jacket]" → "Outside:"
    cleaned = cleaned.replace(/^\[outside[^\]]*\]\s*-?\s*/i, 'Outside: ');
    cleaned = cleaned.replace(/^\[inside[^\]]*\]\s*-?\s*/i, 'Inside: ');
    cleaned = cleaned.replace(/^inside\s+(the\s+)?(jacket|bag|skirt|pants|coat|dress|shirt|top|bottom|lining)[^:]*:\s*-?\s*/i, 'Inside: ');
    cleaned = cleaned.replace(/^outside\s+(the\s+)?(jacket|bag|skirt|pants|coat|dress|shirt|top|bottom)[^:]*:\s*-?\s*/i, 'Outside: ');

    // Clean up "-Overall:" prefix
    cleaned = cleaned.replace(/^-?\s*overall\s*:\s*/i, '');

    // Clean up "Special notes:" prefix (redundant, we're already showing condition)
    cleaned = cleaned.replace(/^special\s+notes?\s*:\s*/i, '');

    // Remove serial number entries entirely (we don't show these)
    // Catches: "Serial:", "Serial number:", "Serial number-", "[Serial]", "[Serial number]", etc.
    if (/^(\[?\s*)?serial\s*(number)?\s*[\]\-:\s]*$/i.test(cleaned) || /^(\[?\s*)?serial\s*(number)?\s*[\]\-:]/i.test(cleaned)) {
      return '';  // Return empty to filter out
    }

    // Remove empty location prefixes that result from cleanup
    cleaned = cleaned.replace(/^(Outside|Inside):\s*$/i, '');

    // Capitalize first letter
    cleaned = cleaned.trim();
    if (cleaned.length > 0) {
      cleaned = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
    }

    return cleaned;
  }

  /**
   * SIMPLE: Extract ALL measurements from raw text directly
   * Finds patterns like: [Label] 30cm (11.81in) or [label: 30cm (11.81in)
   * Returns array of "Label: value" strings
   */
  function extractAllMeasurements(text) {
    const measurements = [];
    const seen = new Set();

    // Pattern 1: [Label] value  - standard bracket format
    // e.g., [Shoulder width] 38cm (14.96in)" or [Waist] 82cm (32.28in)"
    const bracketRegex = /\[([^\]]+)\]\s*(\d+(?:\.\d+)?\s*(?:cm|in|mm)(?:\s*\([^)]+\))?)/gi;
    let match;
    while ((match = bracketRegex.exec(text)) !== null) {
      const label = match[1].trim().toLowerCase();
      const value = match[2].trim().replace(/["''""]+$/, ''); // Remove trailing quotes
      if (label !== 'others' && value && /\d/.test(value)) {
        const capLabel = label.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        const key = capLabel.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          measurements.push(capLabel + ': ' + value);
        }
      }
    }

    // Pattern 2: [label: value - MALFORMED bracket (] was converted to :)
    // e.g., [hem width: 18cm (7.09in)" or [rise: 29cm (11.42in)
    const malformedRegex = /\[([a-z][a-z\s]*?):\s*(\d+(?:\.\d+)?\s*(?:cm|in|mm)(?:\s*\([^)]+\))?)/gi;
    while ((match = malformedRegex.exec(text)) !== null) {
      const label = match[1].trim().toLowerCase();
      const value = match[2].trim().replace(/["''""]+$/, '');
      if (label !== 'others' && value && /\d/.test(value)) {
        const capLabel = label.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        const key = capLabel.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          measurements.push(capLabel + ': ' + value);
        }
      }
    }

    // Pattern 3: Label: value - standard colon format (not inside brackets)
    // For common measurement labels like Height:, Width:, etc.
    const colonLabels = ['height', 'width', 'depth', 'length', 'shoulder', 'shoulder width',
                         'sleeve', 'sleeve length', 'waist', 'bust', 'chest', 'inseam',
                         'thigh width', 'hem width', 'rise', 'outsole', 'insole', 'heel',
                         'handle', 'strap', 'total length'];
    for (const label of colonLabels) {
      const escapedLabel = label.replace(/\s+/g, '\\s+');
      // Use word boundary instead of lookbehind for browser compatibility
      const regex = new RegExp('\\b(' + escapedLabel + ')\\s*:\\s*(\\d+(?:\\.\\d+)?\\s*(?:cm|in|mm)(?:\\s*\\([^)]+\\))?)', 'gi');
      while ((match = regex.exec(text)) !== null) {
        const foundLabel = match[1].trim().toLowerCase();
        const value = match[2].trim().replace(/["''""]+$/, '');
        if (value && /\d/.test(value)) {
          const capLabel = foundLabel.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
          const key = capLabel.toLowerCase();
          if (!seen.has(key)) {
            seen.add(key);
            measurements.push(capLabel + ': ' + value);
          }
        }
      }
    }

    return measurements;
  }

  /**
   * Parse description into structured data
   */
  function parseDescription(html) {
    if (!html) return null;

    const data = {
      condition: [],
      details: {},
      material: null,
      countryOfOrigin: null,
      measurements: [],
      sizeLabel: null,
      authenticity: null,
      description: [], // Free-form description text
      notes: []
    };

    // Normalize HTML to text with line breaks
    let text = html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>\s*<p[^>]*>/gi, '\n\n')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&#039;/g, "'")
      .replace(/&#91;/g, '[')   // HTML entity for [
      .replace(/&#93;/g, ']')   // HTML entity for ]
      .replace(/&#x5B;/gi, '[') // Hex entity for [
      .replace(/&#x5D;/gi, ']') // Hex entity for ]
      .replace(/⇒/g, ':')  // Convert arrows to colons (common in Japanese descriptions)
      .replace(/→/g, ':')  // Another arrow variant
      .replace(/\s+:/g, ':')  // Remove space before colon (e.g., "Overall :" -> "Overall:")
      .replace(/:\s+/g, ': ')  // Normalize space after colon to single space
      // Fix broken bracket format: "Width]" -> "Width:" (missing opening bracket)
      .replace(/\b(Width|Height|Depth|Handle|Shoulder|Length)\]/gi, '$1:')
      // Fix leftover opening brackets before measurements: "[Height:" -> "Height:"
      .replace(/\[(Width|Height|Depth|Handle|Shoulder|Length):/gi, '$1:')
      // Remove stray closing brackets after values (e.g., "Brand: HERMES]" -> "Brand: HERMES")
      .replace(/:\s*([^:\[\]\n]+)\]/g, ': $1')
      // Remove stray opening brackets before keys with trailing spaces (e.g., "[Category ]" -> "Category:")
      // But NOT measurement brackets which should be preserved for bracket split
      .replace(/\[(?!(Others|Width|Height|Depth|Length|Total|Handle|Shoulder|Strap|Bust|Waist|Hips|Inseam|Outsole|Insole|Heel|See|Thigh|Hem|Rise|Chest|Sleeve|Back|Front|Skirt|Arm|Cuff)\s*\]?)([A-Za-z]+)\s+\]/gi, '$2:')
      .trim();

    // === SPLIT DASH-PREFIX SECTION MARKERS ===
    // Split "-Material", "-Color", etc. to their own lines BEFORE bracket processing
    text = text.replace(/\s*-\s*(Material|Color|Condition|Accessories|Notes?|Remarks?)\s*(?=\[|$)/gi, '\n-$1 ');

    // === HANDLE RUN-ON BRACKET FORMAT ===
    // Split "[Brand] HERMES [Engraving] W engraved..." into separate lines
    text = text.replace(/\s*\[([^\]]+)\]\s*/g, '\n[$1] ');

    // === HANDLE DASH-PREFIX BRACKET-VALUE FORMAT ===
    // Convert "-Notation size [40 CN170 80A]" to "Notation size: 40 CN170 80A"
    text = text.replace(/-\s*Notation\s+size\s*\[([^\]]+)\]/gi, '\nNotation size: $1');
    text = text.replace(/-\s*Size\s+indicated\s*\[([^\]]+)\]/gi, '\nSize indicated: $1');

    // Handle "-Notation size XL" without brackets (common format)
    // Match: -Notation size followed by size value (letters/numbers) until end of line or next field
    text = text.replace(/-\s*Notation\s+size\s+([A-Z0-9]+(?:\s*\([^)]+\))?)\s*(?=\n|-|$|\[)/gi, '\nNotation size: $1\n');

    // Extract size from "-Item name [... size 40 L ...]" format BEFORE skipping item name lines
    // This catches "size 40 L" or "size M" patterns in the item name
    // But NOT dimension patterns like "Size 30 x 235cm" (x is dimension separator, not size letter)
    const itemNameSizeMatch = text.match(/-\s*Item\s+name\s*\[[^\]]*\bsize\s+(\d+(?:\.\d+)?(?:\s*[A-WYZ])?|\b(?:XXS|XS|SM|S|M|L|XL|XXL|XXXL)\b)[^\]]*\]/i);
    if (itemNameSizeMatch) {
      // Additional check: don't extract if followed by "x" and another number (dimension pattern)
      const fullMatch = text.match(/-\s*Item\s+name\s*\[[^\]]*\bsize\s+\d+(?:\.\d+)?\s*x\s*\d+/i);
      if (!fullMatch) {
        text = text + '\nExtracted size: ' + itemNameSizeMatch[1].trim();
      }
    }

    // Normalize "Notation color:" to "Color:"
    text = text.replace(/Notation\s+colou?r\s*:/gi, 'Color:');

    // === HANDLE RUN-ON KEY:VALUE FORMAT ===
    // First, protect compound patterns from being split
    text = text.replace(/Notation\s+size\s*:/gi, 'NOTATION_SIZE_PLACEHOLDER:');
    text = text.replace(/Size\s+indicated/gi, 'SIZE_INDICATED_PLACEHOLDER');
    text = text.replace(/Belt\s+size\s*:/gi, 'BELT_SIZE_PLACEHOLDER:');
    text = text.replace(/Base\s+length\s*:/gi, 'BASE_LENGTH_PLACEHOLDER:');

    // Split "Brand: X Color: Y Material: Z" into separate lines
    // Also handles Specifications and Detailed Description
    // Use \s* (optional spaces) to handle run-on text like "RedSpecifications:"
    // Use negative lookbehind to avoid matching "Color:" inside "Hardware Color:"
    // Use [ \t]* after colon (not \s*) to preserve newlines for multi-line formats
    text = text.replace(/(?<!Hardware\s)(Brand|Color|Material|Country of origin|Size|Accessories|Weight|Condition|Engraving|Engraved|Specifications|Detailed\s+Description)\s*[：:][ \t]*/gi, '\n$1: ');

    // Restore the protected patterns
    text = text.replace(/NOTATION_SIZE_PLACEHOLDER:/gi, 'Notation size:');
    text = text.replace(/SIZE_INDICATED_PLACEHOLDER/gi, 'Size indicated');
    text = text.replace(/BELT_SIZE_PLACEHOLDER:/gi, 'Belt size:');
    text = text.replace(/BASE_LENGTH_PLACEHOLDER:/gi, 'Base length:');

    // Handle inline dash format: "- Damage details ... - Item Details ... - Size Details"
    // Split these into separate lines - handle all common section headers
    text = text.replace(/\s+-\s+(Item\s+Name|Damage\s+details?|Item\s+Details?|Size\s+Details?|Material)/gi, '\n- $1');

    // Handle "About size" or "About Size" followed by text - split into new line but keep content before
    text = text.replace(/\s+(About\s+[Ss]ize)\s+/gi, '\n$1\n');

    // Split on standalone key: value patterns that are concatenated
    // e.g., "Brand: Balenciaga Accessories :" -> separate lines
    // Use [ \t]* after colon to preserve newlines for multi-line formats
    text = text.replace(/\s+(Category|Accessories)\s*[：:][ \t]*/gi, '\n$1: ');

    // Split freeform description fields so they become separate lines to be filtered
    // e.g., "None Details: Hardware mark..." -> "None\nDetails: Hardware mark..."
    // This ensures "Accessories: None" is properly parsed before the Details section
    text = text.replace(/\s+(Details?|Comments?|About\s+Us)\s*[：:\-][ \t]*/gi, '\n$1: ');

    // NOTE: B:/W:/H: -> Bust:/Waist:/Hips: conversion is done ONLY in formatMeasurements()
    // to avoid converting condition ranks like "B: There is some wear" to "Bust: There is some wear"

    // Split concatenated measurements like "Length:35 Width :26 Depth :25"
    // Use negative lookbehind to avoid splitting compound keys like "Base Length:" or "Belt size:"
    text = text.replace(/(?<![A-Za-z])\s+(Length|Width|Depth|Height|Handlebars?|Handle|Strap|Drop|Handle\/Drop|Waist|Hips?|Bust|Inseam|Total\s*Length|Number\s*of\s*holes?|Buckle)\s*[：:]\s*/gi, '\n$1: ');

    // Handle "Handle/Drop Length:" as a single measurement key
    text = text.replace(/Handle\/Drop\s*\n\s*Length\s*:/gi, 'Handle Drop:');

    // Split "Handlebars: XXcm Handle Drop:" into separate lines
    text = text.replace(/(Handlebars?\s*:\s*\d+(?:\.\d+)?(?:cm|in)?(?:\s*\([^)]+\))?)\s+(Handle\s*(?:Drop|Length))/gi, '$1\n$2');

    // Split measurement lines that are concatenated (e.g., "Outsole: 29.5cm (11.61in) Instep width: 10.5cm")
    // Look for patterns like "measurement) NextKey:" and add line break
    text = text.replace(/(\d+(?:\.\d+)?(?:cm|in|mm)\s*(?:\([^)]+\))?)\s+([A-Z][a-z]+(?:\s+[a-z]+)?)\s*:/g, '$1\n$2:');

    // Split before "Detail Remarks:" and remove it
    text = text.replace(/\s*Detail\s+Remarks\s*:\s*/gi, '\n');

    // Split #XX size indicators that are stuck at end of measurements
    // e.g., "3cm (1.18in) #85" -> "3cm (1.18in)\nSize: 85"
    text = text.replace(/(\d+(?:\.\d+)?(?:cm|in|mm)\s*(?:\([^)]+\))?)\s+#(\d+)\b/gi, '$1\nSize: $2');

    // Split #XX size indicators at BEGINNING of lines followed by measurement labels
    // e.g., "#36 Sleeve length: 35cm" -> "Size: 36\nSleeve length: 35cm"
    text = text.replace(/#(\d+)\s+(Sleeve|Length|Width|Height|Bust|Waist|B|W|H)\s*:/gi, 'Size: $1\n$2:');

    // Also split "Size indicated:XX Reference Size" pattern
    text = text.replace(/(Size indicated:[^R]+)(Reference)/gi, '$1\n$2');

    // Split run-on condition text: "Detail Remarks: XXX [Size indicated" -> separate lines
    text = text.replace(/\s*\[(Size indicated|Detail|Condition|Weight|Accessories)\b/gi, '\n[$1');

    // Clean bullet points - remove bullets but keep line structure
    // We'll remove all bullets and re-add consistent formatting later
    // Include Japanese bullet point (・) and other bullet variants
    text = text.replace(/^[•\-・]\s*/gm, '');
    text = text.replace(/・/g, '');  // Remove inline Japanese bullets too

    // === SIMPLE EXTRACTION: Grab ALL measurements from raw text FIRST ===
    // This catches everything before complex parsing can lose them
    const directMeasurements = extractAllMeasurements(text);

    // Track which measurement keys we've already extracted cleanly
    // This prevents line parsing from adding dirty/duplicate values
    const extractedMeasurementKeys = new Set(directMeasurements.map(m => {
      const match = m.match(/^([^:]+):/);
      return match ? match[1].toLowerCase().trim() : '';
    }));

    const lines = text.split('\n').map(l => l.trim()).filter(l => l);

    // Detect format type
    const hasBulletFormat = lines.some(l => /^[•●]\s*(damage|item|size)\s*details?/i.test(l));
    const hasBracketFormat = lines.some(l => /^\[[^\]]+\]/.test(l));
    const hasDashFormat = lines.some(l => /^-\s*(size|material|item condition|included)/i.test(l));
    const hasParagraphFormat = !hasBulletFormat && !hasBracketFormat && !hasDashFormat;

    let currentSection = null;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Skip useless lines
      if (shouldSkipLine(line)) continue;

      // Skip product title lines (contain brand + SIZE + dimensions)
      if (/\b(gucci|louis vuitton|chanel|hermes|prada|dior|fendi|balenciaga|burberry|celine|bottega|loewe|valentino|givenchy|ysl|saint laurent)\b/i.test(line) &&
          /\bSIZE\s+\d/i.test(line)) {
        continue;
      }

      // === SECTION HEADERS ===

      // Bullet format: • or ● Damage details, Item details, Size details
      if (/^[•●]\s*damage\s*details?/i.test(line)) {
        currentSection = 'condition';
        continue;
      }
      if (/^[•●]\s*item\s*details?/i.test(line)) {
        currentSection = 'details';
        continue;
      }
      if (/^[•●]\s*size\s*details?/i.test(line)) {
        currentSection = 'size';
        continue;
      }

      // Handle "●Size" alone as a section header (size info on following lines)
      if (/^[•●]\s*size\s*$/i.test(line)) {
        currentSection = 'size';
        continue;
      }

      // Handle "●Size [Notation] XX [Actual size] measurements..." format
      // e.g., "●Size [Notation] 34 [Actual size] Width: approx. 39cm..."
      const bulletSizeNotationMatch = line.match(/^[●•]\s*Size\s+\[Notation\]\s*(\d+(?:\.\d+)?|XXS|XS|SM|S|M|L|XL|XXL|XXXL|OS)\s*(?:\[Actual\s*size\])?\s*(.*)/i);
      if (bulletSizeNotationMatch) {
        currentSection = 'size';
        // Extract the size label
        if (!data.sizeLabel) {
          data.sizeLabel = bulletSizeNotationMatch[1].trim();
        }
        // Process any measurements after [Actual size]
        const afterSize = bulletSizeNotationMatch[2].trim();
        if (afterSize) {
          // Split measurements like "Width: approx. 39cm Length: approx. 57cm"
          const measureParts = afterSize.split(/\s+(?=(?:Width|Length|Height|Depth|Shoulder|Bust|Waist|Chest|Sleeve|Inseam)\s*:)/i);
          for (const part of measureParts) {
            if (part.trim() && /\d/.test(part)) {
              data.measurements.push(part.trim());
            }
          }
        }
        continue;
      }

      // Dash format: - Size-., - Material-., - Item Condition, - Size Details
      if (/^-\s*size\s*(details?)?\s*-?\.?/i.test(line)) {
        currentSection = 'size';
        // Check if there's content after the header on the same line
        const afterHeader = line.replace(/^-\s*size\s*(details?)?\s*-?\.?\s*/i, '').trim();
        if (afterHeader && afterHeader.length > 0) {
          // Split any remaining measurements
          const parts = afterHeader.split(/(?<=\))\s+(?=[A-Z])/);
          for (const part of parts) {
            if (isMeasurementLine(part) || /size indicated/i.test(part)) {
              if (!/reference size/i.test(part)) {
                data.measurements.push(part);
              }
            }
          }
        }
        continue;
      }
      if (/^-\s*material\s*-?\.?$/i.test(line)) {
        currentSection = 'material';
        continue;
      }
      // Handle "Material: - Please note..." or "Material: None" - skip the note, just mark section
      if (/^-?\s*material\s*[：:]\s*(-\s*please\s+note|none|\-)/i.test(line)) {
        currentSection = 'material';
        continue;
      }
      if (/^-\s*(item\s*)?(condition|damage)\s*(details?)?/i.test(line)) {
        currentSection = 'condition';
        // Check if there's content after the header on the same line
        const afterHeader = line.replace(/^-\s*(item\s*)?(condition|damage)\s*(details?)?\s*/i, '').trim();
        if (afterHeader) {
          // Split multiple condition items like "Back: Stain Inside: Scratch"
          // Look for patterns like "Location: Issue" and split them
          const conditionParts = afterHeader.split(/\s+(?=[A-Z][a-z]*\s*:)/);
          for (const part of conditionParts) {
            if (part.trim()) {
              data.condition.push(part.trim());
            }
          }
        }
        continue;
      }
      if (/^-\s*item\s*details?\s*/i.test(line)) {
        currentSection = 'details';
        continue;
      }
      // Skip "- Item Name BRAND Product Name" lines - this is just the product title
      if (/^-\s*item\s*name\s+/i.test(line)) {
        continue;
      }
      if (/^-\s*(included|what'?s?\s*included)/i.test(line)) {
        currentSection = 'accessories';
        continue;
      }
      if (/^-\s*notes?\s*-?\.?$/i.test(line)) {
        currentSection = 'notes';
        continue;
      }

      // === BRACKET FORMAT: [Brand], [Material], etc. ===
      // Also handle incomplete brackets like "[Detailed" (missing closing bracket)
      const bracketMatch = line.match(/^\[([^\]]+)\]?\s*(.*)$/);
      if (bracketMatch && line.startsWith('[')) {
        // Normalize key: lowercase, trim, and collapse multiple/non-standard spaces to single space
        const key = bracketMatch[1].toLowerCase().trim().replace(/[\s\u00A0\u2000-\u200A]+/g, ' ');
        const value = bracketMatch[2].trim();

        if (key === 'actual size' || key.startsWith('actual size') || key === 'size') {
          currentSection = 'size';
          if (value) {
            // Check if value is a ring size format like "No. 9.5" or "#12"
            const ringNoMatch = value.match(/^(?:No\.?\s*|#)?(\d+(?:\.\d+)?)\s*$/i);
            if (ringNoMatch && !data.sizeLabel) {
              data.sizeLabel = ringNoMatch[1];
            } else {
              // Split multiple measurements on the same line (e.g., "Width: 57.5cm, Height: 78cm")
              const parts = value.split(/,\s*/).filter(p => p.trim());
              for (const part of parts) {
                data.measurements.push(part.trim());
              }
            }
          }
        } else if (key === 'height' || key === 'width' || key === 'depth' || key === 'length' ||
                   key === 'total length' || key === 'handle' || key === 'shoulder' || key === 'strap' ||
                   key === 'bust' || key === 'waist' || key === 'hips' || key === 'inseam' ||
                   key === 'outsole' || key === 'insole' || key === 'heel' || key === 'others' ||
                   // Clothing measurements (exact matches)
                   key === 'thigh width' || key === 'hem width' || key === 'rise' ||
                   key === 'chest' || key === 'sleeve' || key === 'sleeve length' ||
                   key === 'shoulder width' || key === 'back length' || key === 'front length' ||
                   key === 'skirt length' || key === 'arm hole' || key === 'cuff' ||
                   // Fallback: any key containing measurement words + value has numbers
                   (/\b(width|height|depth|length|waist|bust|chest|inseam|shoulder|sleeve|thigh|hem|rise|strap|handle)\b/i.test(key) && /\d/.test(value))) {
          // Measurement bracket field like [Height] 30cm or [Total length] 235cm - add to measurements
          // Skip "others" if value is empty or doesn't contain actual measurements
          if (key === 'others' && (!value || !/\d/.test(value))) {
            currentSection = 'size';
            continue;
          }
          if (value && /\d/.test(value)) {
            // Normalize key: "total length" -> "Length", "others" -> "Other"
            // Capitalize each word for compound keys like "thigh width" -> "Thigh Width"
            let measureKey = key;
            if (key === 'total length') measureKey = 'Length';
            else if (key === 'others') measureKey = 'Other';
            else measureKey = key.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

            // Clean up value - extract the FIRST measurement value (before any brackets)
            let cleanValue = value;
            const firstMeasureMatch = cleanValue.match(/^(\d+(?:\.\d+)?\s*(?:cm|in|mm)(?:\s*\([^)]+\))?)/i);
            if (firstMeasureMatch) {
              cleanValue = firstMeasureMatch[1];
            } else {
              // Fallback: Remove any bracket content like "[Others]" or "[See image]" that might be stuck
              cleanValue = cleanValue.replace(/\s*\[[^\]]*\]\s*/g, ' ').trim();
            }
            // Remove any line markers like "-Material" or "-Color" that got stuck
            cleanValue = cleanValue.replace(/\s*-\s*(Material|Color|See\s+image|Condition|Accessories|Notes?).*$/gi, '').trim();
            // Remove any "Others:" or similar that might have leaked through
            cleanValue = cleanValue.replace(/\s*Others\s*:?\s*$/gi, '').trim();
            // Remove trailing quotes (ASCII and Unicode curly quotes) - do this LAST
            cleanValue = cleanValue.replace(/["'""'']+$/g, '').trim();

            // Skip if this key was already extracted cleanly by extractAllMeasurements
            if (!extractedMeasurementKeys.has(measureKey.toLowerCase())) {
              data.measurements.push(measureKey + ': ' + cleanValue);
            }
          }
          currentSection = 'size';
        } else if (key === 'material' || key === 'material notation') {
          data.material = value || null;
          currentSection = 'material';
        } else if (key === 'condition' || key === 'detailed condition' || key === 'detailed' || key.startsWith('detailed') ||
                   key === 'outside' || key === 'inside' || key === 'special note' || key === 'special notes') {
          // These are all condition section markers
          currentSection = 'condition';
          // Add the section header as a label (e.g., "[Outside]" becomes "Outside:")
          if (key === 'outside' || key === 'inside' || key === 'special note' || key === 'special notes') {
            const label = key.charAt(0).toUpperCase() + key.slice(1) + ':';
            data.condition.push(label);
          }
          if (value) data.condition.push(value);
        } else if (key === 'item rank') {
          // Skip item rank - we don't use this
          continue;
        } else if (key === 'product number') {
          // Skip product numbers - not useful for customers
          continue;
        } else if (key === 'color' || key === 'colour' || key === 'notation color' || key === 'notation colour') {
          if (value) data.details.Color = value;
          else currentSection = 'color';
        } else if (key === 'hardware color' || key === 'hardware colour') {
          if (value) data.details['Hardware Color'] = value;
        } else if (key === 'brand') {
          if (value) data.details.Brand = value;
          else currentSection = 'brand';
        } else if (key === 'type' || key === 'category') {
          if (value) data.details.Category = value;
          else currentSection = 'category';
        } else if (key === 'accessories') {
          if (value) {
            // Append to existing accessories
            if (data.details.Accessories && data.details.Accessories !== '-') {
              data.details.Accessories += ', ' + value;
            } else {
              data.details.Accessories = value;
            }
          }
          // Always set currentSection so subsequent lines get appended
          currentSection = 'accessories';
        } else if (key === 'serial' || key === 'serial number') {
          data.details.Serial = value;
        } else if (key === 'weight') {
          data.details.Weight = value;
        } else if (key === 'stone') {
          data.details.Stone = value;
        } else if (key === 'country' || key === 'country of origin') {
          data.countryOfOrigin = value;
        } else if (key === 'comment') {
          if (value) data.notes.push(value);
        } else if (key === 'remarks' || key === 'condition rank') {
          // [Remarks] and [Condition Rank] are condition section headers
          currentSection = 'condition';
          // Skip empty values like "." or just whitespace
          if (value && value.length > 2 && !/^\.+$/.test(value.trim())) {
            data.condition.push(value);
          }
        } else if (key === 'size indicated' || key === 'size indicated by manufacturer' ||
                   key === 'notation size' || key === 'size notation' ||
                   key === 'notation' || key === 'extracted size') {
          // Size indicated fields should go to sizeLabel (includes [Notation] XX format)
          if (value && !data.sizeLabel) {
            // Skip useless placeholder values
            if (/^none/i.test(value) || /please\s+refer/i.test(value) || /^-+$/.test(value) || /^n\/a$/i.test(value)) {
              continue;
            }
            let sizeVal = value.trim();

            // FIRST: Check for "Tag notation X size" pattern where X is a letter size
            // e.g., "Tag notation S size 175 92A" -> "S"
            const tagNotationMatch = sizeVal.match(/tag\s+notation\s+(XXS|XS|SM|S|M|L|XL|XXL|XXXL|OS)\s+size/i);
            if (tagNotationMatch) {
              sizeVal = tagNotationMatch[1].toUpperCase();
            } else {
              // Check for standalone letter sizes anywhere in the string
              const letterSizeMatch = sizeVal.match(/\b(XXS|XS|SM|S|M|L|XL|XXL|XXXL|OS)\b/i);
              if (letterSizeMatch && !/\d+\s*cm/i.test(sizeVal)) {
                // Found a letter size and it's not just a dimension pattern
                sizeVal = letterSizeMatch[1].toUpperCase();
              } else {
                // Fall back to numeric extraction - "40 CN170 80A" -> "40"
                const primarySizeMatch = sizeVal.match(/^(\d+(?:\.\d+)?)\s*([A-Z](?:\s|$))?/i);
                if (primarySizeMatch) {
                  sizeVal = primarySizeMatch[1] + (primarySizeMatch[2] ? ' ' + primarySizeMatch[2].trim() : '');
                  sizeVal = sizeVal.trim();
                }
              }
            }
            sizeVal = sizeVal.replace(/^#/, ''); // Remove # prefix
            sizeVal = sizeVal.replace(/^(\d+)\s+1\s+2(\s*[A-Z])?$/i, (_, num, letter) => {
              return num + '.5' + (letter || '');
            });
            data.sizeLabel = sizeVal;
          }
        } else {
          // Unknown bracket field - store with capitalized key
          const capKey = key.charAt(0).toUpperCase() + key.slice(1);
          data.details[capKey] = value;
        }
        continue;
      }

      // === HANDLE "Actual size (cm):" EARLY - before KV parsing ===
      // Some products have this format with measurements after it
      const actualSizeMatch = line.match(/^Actual\s+size\s*(?:\([^)]*\))?\s*:\s*(.+)$/i);
      if (actualSizeMatch) {
        const measurements = actualSizeMatch[1].trim();
        if (measurements) {
          // Split multiple measurements on the same line (e.g., "Width: 57.5cm, Height: 78cm")
          const parts = measurements.split(/,\s*/).filter(p => p.trim());
          for (const part of parts) {
            data.measurements.push(part.trim());
          }
        }
        continue;
      }

      // === HANDLE PARENTHETICAL PREFIX FORMAT: (Field) Value ===
      // e.g., "(Size) Monogram Canvas", "(Color) Brown", "(Material) Leather", "(Type) Model Name"
      const parenMatch = line.match(/^\(([^)]+)\)\s*(.*)$/);
      if (parenMatch) {
        const field = parenMatch[1].toLowerCase().trim();
        const value = parenMatch[2].trim();

        if (field === 'size' || field === 'actual size') {
          currentSection = 'size';
          // Check if value contains dimensions or is just a material name
          if (value && /\d/.test(value)) {
            // Has numbers - likely dimensions, add to measurements
            data.measurements.push(value);
          }
          // If it's just a material name like "Monogram Canvas", ignore it (redundant with material field)
          continue;
        } else if (field === 'color' || field === 'colour') {
          if (value && !data.details.Color) {
            data.details.Color = value;
          }
          continue;
        } else if (field === 'material') {
          if (value && !data.material) {
            data.material = value;
          }
          continue;
        } else if (field === 'type' || field === 'model') {
          // (Type) field contains model name/product identifier
          if (value && !data.details['Model Name']) {
            data.details['Model Name'] = value;
          }
          continue;
        } else if (field === 'condition') {
          currentSection = 'condition';
          if (value) {
            data.condition.push(value);
          }
          continue;
        }
        // Unknown parenthetical field - skip
        continue;
      }

      // === HANDLE SECTION HEADERS WITH NO VALUE (e.g., "- Accessories:") ===
      // These have a colon but nothing after it, so the KV regex won't match
      const sectionHeaderMatch = line.match(/^-?\s*(Accessories|Condition|Material|Color)\s*:\s*$/i);
      if (sectionHeaderMatch) {
        const sectionName = sectionHeaderMatch[1].toLowerCase();
        if (sectionName === 'accessories') {
          currentSection = 'accessories';
        } else if (sectionName === 'condition') {
          currentSection = 'condition';
        } else if (sectionName === 'material') {
          currentSection = 'material';
        } else if (sectionName === 'color') {
          currentSection = 'color';
        }
        continue;
      }

      // === KEY: VALUE FORMAT ===
      const kvMatch = line.match(/^([^:]+):\s*(.+)$/);
      if (kvMatch) {
        // Strip bullet characters (● and •) and dash prefixes from key before normalizing
        const key = kvMatch[1].replace(/^[-●•]\s*/, '').toLowerCase().trim();
        const value = kvMatch[2].trim();

        // Check if this is a condition-related key:value line
        // Expanded list of condition location markers
        const conditionLocationKeys = ['condition', 'damage', 'overall', 'outside', 'inside',
          'detailed description', 'bottom', 'side', 'corner', 'corners', 'edge', 'edges',
          'surface', 'hardware', 'zipper', 'zipper handle', 'main mouth', 'main opening',
          'opening/closing opening', 'special note', 'special notes', 'state/condition',
          'overall condition', 'outside overall', 'inside overall'];

        // Don't treat "hardware color" as a condition key even though it contains "hardware"
        const isConditionKey = conditionLocationKeys.some(ck => key === ck || key.includes(ck)) &&
                               !key.includes('color') && !key.includes('colour');

        if (isConditionKey) {
          // "Condition remarks:" is a section header - set section and add value if present
          if (key === 'condition remarks' || key === 'condition remark' || key === 'state/condition') {
            currentSection = 'condition';
            if (value && value.length > 2) {
              data.condition.push(value);
            }
          } else {
            // Regular condition line like "Overall: A rank" or "Outside: Used A rank..."
            // Format nicely: "Outside: Used A rank (details)" -> "Outside: details"
            let conditionText = value;
            // Clean up rank prefixes like "A rank" or "Used A rank"
            conditionText = conditionText.replace(/^(?:Used\s+)?[A-Z]\s+rank\s*/i, '');
            if (conditionText && conditionText.length > 2) {
              // Add label for location-based entries
              const labelKeys = ['outside', 'inside', 'bottom', 'side', 'corner', 'corners',
                'edge', 'edges', 'surface', 'hardware', 'zipper', 'zipper handle',
                'main mouth', 'main opening', 'opening/closing opening'];
              if (labelKeys.includes(key)) {
                const label = key.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                data.condition.push(label + ': ' + conditionText);
              } else {
                data.condition.push(conditionText);
              }
            }
          }
          continue;
        }

        // Skip "Name:" lines - these are product titles, not useful info
        if (key === 'name' || key === 'item name') {
          continue;
        }

        // Skip product number lines
        if (key === 'product number') {
          continue;
        }

        // Handle size notation fields - extract size and add to size section
        if (key === 'notation size' || key === 'size notation' || key === 'notation' ||
            key === 'size indicated' || key === 'size indicated by manufacturer' ||
            key === 'extracted size') {
          if (value && !data.sizeLabel) {
            // Skip useless placeholder values
            if (/^none/i.test(value) || /please\s+refer/i.test(value) || /^-+$/.test(value) || /^n\/a$/i.test(value)) {
              continue;
            }
            let sizeVal = value.trim();

            // FIRST: Check for "Tag notation X size" pattern where X is a letter size
            // e.g., "Tag notation S size 175 92A" -> "S"
            const tagNotationMatch = sizeVal.match(/tag\s+notation\s+(XXS|XS|SM|S|M|L|XL|XXL|XXXL|OS)\s+size/i);
            if (tagNotationMatch) {
              sizeVal = tagNotationMatch[1].toUpperCase();
            } else {
              // Check for standalone letter sizes anywhere in the string
              const letterSizeMatch = sizeVal.match(/\b(XXS|XS|SM|S|M|L|XL|XXL|XXXL|OS)\b/i);
              if (letterSizeMatch && !/\d+\s*cm/i.test(sizeVal)) {
                // Found a letter size and it's not just a dimension pattern
                sizeVal = letterSizeMatch[1].toUpperCase();
              } else {
                // Fall back to numeric extraction - "40 CN170 80A" -> "40"
                const primarySizeMatch = sizeVal.match(/^(\d+(?:\.\d+)?)\s*([A-Z](?:\s|$))?/i);
                if (primarySizeMatch) {
                  sizeVal = primarySizeMatch[1] + (primarySizeMatch[2] ? ' ' + primarySizeMatch[2].trim() : '');
                  sizeVal = sizeVal.trim();
                }
                // Clean up the value - remove cm measurements, just keep size number
                sizeVal = sizeVal.replace(/\s*\d+(?:\.\d+)?\s*cm.*$/i, '').trim();
              }
            }
            sizeVal = sizeVal.replace(/\s*E$/i, ' E').trim(); // Normalize "41 E" format
            // Convert "36 1 2" to "36.5" for half sizes
            sizeVal = sizeVal.replace(/^(\d+)\s+1\s+2(\s*[A-Z])?$/i, (m, num, letter) => {
              return num + '.5' + (letter || '');
            });
            // Handle "#85" format - clean up the hash
            sizeVal = sizeVal.replace(/^#/, '');
            if (sizeVal) data.sizeLabel = sizeVal;
          }
          continue;
        }

        // Reference size lines - add to measurements (they contain actual measurements)
        if (key === 'reference size' || key === 'measurement size' ||
            key.includes('reference size') || key.startsWith('reference size')) {
          if (value && /\d/.test(value)) {
            data.measurements.push('Size: ' + value);
          }
          continue;
        }

        // Handle "Size:" or "Size (h x w x d):" as a section header
        if (key === 'size' || key.startsWith('size (') || key.startsWith('size(')) {
          currentSection = 'size';
          // FIRST: Check for "Tag notation X size" pattern where X is a letter size
          // e.g., "Tag notation S size 175 92A" -> "S"
          const tagNotationMatch = value.match(/tag\s+notation\s+(XXS|XS|SM|S|M|L|XL|XXL|XXXL|OS)\s+size/i);
          if (tagNotationMatch && !data.sizeLabel) {
            data.sizeLabel = tagNotationMatch[1].toUpperCase();
          }
          // Check if value contains "Notation size: XX" pattern
          else if (!data.sizeLabel) {
            const notationMatch = value.match(/notation\s*size\s*:\s*(.+)/i);
            if (notationMatch) {
              let sizeVal = notationMatch[1].trim();
              sizeVal = sizeVal.replace(/^#/, ''); // Remove # prefix
              sizeVal = sizeVal.replace(/^(\d+)\s+1\s+2(\s*[A-Z])?$/i, (_, num, letter) => {
                return num + '.5' + (letter || '');
              });
              data.sizeLabel = sizeVal;
            }
          }
          if (value && !data.sizeLabel) {
            // Check for EU/US shoe size pattern: "EU40(around 25cm...)" or "US7(25.5cm)"
            const shoeSizeMatch = value.match(/^(EU\s*\d+(?:\.\d+)?|US\s*\d+(?:\.\d+)?|UK\s*\d+(?:\.\d+)?)\s*\(/i);
            if (shoeSizeMatch && !data.sizeLabel) {
              data.sizeLabel = shoeSizeMatch[1].replace(/\s+/g, ' ').trim();
              // Optionally extract insole measurement if present
              const insoleMatch = value.match(/(\d+(?:\.\d+)?)\s*cm/i);
              if (insoleMatch) {
                data.measurements.push('Insole: ' + insoleMatch[1] + 'cm');
              }
            } else if (/cm|in|mm|"/i.test(value)) {
              // If there's a value and it contains measurements, add it
              // Clean up "Approximately", "Approx.", or "(Approx.)" prefix
              let cleanValue = value.replace(/^(?:\()?approx(?:imately)?\.?\)?[\s:]*/i, '').trim();

              // Parse "W approx. 30cm (11.81in) x H approx. 23cm (9.06in) x D approx. 8cm (3.15in)" format
              // Captures both cm and inch values from original string
              const whdMatch = cleanValue.match(/W\s*(?:approx\.?\s*)?(\d+(?:\.\d+)?)\s*cm\s*\((\d+(?:\.\d+)?)\s*in\)\s*x\s*H\s*(?:approx\.?\s*)?(\d+(?:\.\d+)?)\s*cm\s*\((\d+(?:\.\d+)?)\s*in\)\s*x\s*D\s*(?:approx\.?\s*)?(\d+(?:\.\d+)?)\s*cm\s*\((\d+(?:\.\d+)?)\s*in\)/i);
              if (whdMatch) {
                data.measurements.push('Width: ' + whdMatch[1] + ' cm (' + whdMatch[2] + ' in)');
                data.measurements.push('Height: ' + whdMatch[3] + ' cm (' + whdMatch[4] + ' in)');
                data.measurements.push('Depth: ' + whdMatch[5] + ' cm (' + whdMatch[6] + ' in)');
              }
              // Parse "W30 x H22.5 x D9cm (3.54in)" format - dimensions without cm after each, single inch at end
              // Also handles: "Approximately W30 x H22.5 x D9cm" (no inch value)
              const whdCompactMatch = cleanValue.match(/W\s*(\d+(?:\.\d+)?)\s*x\s*H\s*(\d+(?:\.\d+)?)\s*x\s*D\s*(\d+(?:\.\d+)?)\s*cm/i);
              if (!whdMatch && whdCompactMatch) {
                const wVal = whdCompactMatch[1];
                const hVal = whdCompactMatch[2];
                const dVal = whdCompactMatch[3];
                const wIn = (parseFloat(wVal) / 2.54).toFixed(2);
                const hIn = (parseFloat(hVal) / 2.54).toFixed(2);
                const dIn = (parseFloat(dVal) / 2.54).toFixed(2);
                data.measurements.push('Width: ' + wVal + ' cm (' + wIn + ' in)');
                data.measurements.push('Height: ' + hVal + ' cm (' + hIn + ' in)');
                data.measurements.push('Depth: ' + dVal + ' cm (' + dIn + ' in)');
              } else if (!whdMatch) {
                // Parse "approx. 24cm (9.45in) x 30cm (11.81in) x 15.5cm (6.10in)" format into separate dimensions
                // Also handles "approximately 22.5cm x 30.5cm x 8cm" and simpler "24cm x 30cm x 15.5cm" format
                const dimMatch = cleanValue.match(/^(\d+(?:\.\d+)?)\s*cm\s*(?:\([^)]+\))?\s*x\s*(\d+(?:\.\d+)?)\s*cm\s*(?:\([^)]+\))?\s*x\s*(\d+(?:\.\d+)?)\s*cm/i);
                if (dimMatch) {
                  data.measurements.push('Height: ' + dimMatch[1] + 'cm');
                  data.measurements.push('Width: ' + dimMatch[2] + 'cm');
                  data.measurements.push('Depth: ' + dimMatch[3] + 'cm');
                } else {
                  // Not a 3-dimension format, just add as-is
                  data.measurements.push(cleanValue);
                }
              }
            } else if (!data.sizeLabel) {
              // It's a size label like "42"
              let sizeVal = value.trim();
              sizeVal = sizeVal.replace(/^(\d+)\s+1\s+2(\s*[A-Z])?$/i, (_, num, letter) => {
                return num + '.5' + (letter || '');
              });
              data.sizeLabel = sizeVal;
            }
          }
          continue;
        }

        // Handle "Color:" as a section header (value may come on next line)
        if (key === 'color' || key === 'colour') {
          currentSection = 'color';
          if (value) {
            // Clean up stray periods/punctuation at the start (e.g., ". Red" -> "Red")
            let cleanColor = value.replace(/^[\.\s]+/, '').trim();
            data.details.Color = cleanColor;
          }
          continue;
        }

        // Handle "Actual size (cm):" - this contains measurements
        if (key === 'actual size' || key === 'actual size (cm)' || key.startsWith('actual size')) {
          if (value) data.measurements.push(value);
          continue;
        }

        // Check if it's a measurement (but not "size: 41 E" type)
        if (isMeasurementLine(line) && key !== 'size') {
          // Normalize "Vertical" to "Height"
          const normalizedLine = line.replace(/^vertical\s*:/i, 'Height:');
          data.measurements.push(normalizedLine);
          continue;
        }

        // Known detail fields
        if (key === 'brand' || key === 'brand name') { data.details.Brand = value; continue; }
        if (key === 'model' || key === 'model name' || key === 'item name model') { data.details['Model Name'] = value; continue; }
        if (key === 'series' || key === 'line') { data.details.Series = value; continue; }
        if (key === 'serial number' || key === 'serial') { data.details.Serial = value; continue; }
        if (key === 'shape number') { data.details['Shape Number'] = value; continue; }
        if (key === 'color' || key === 'colour' || key === 'notation color' || key === 'notation colour') { data.details.Color = value; continue; }
        if (key === 'hardware color' || key === 'hardware colour') { data.details['Hardware Color'] = value; continue; }
        if (key === 'category' || key === 'type') { data.details.Category = value; continue; }
        if (key === 'item name') { continue; } // Skip product title repeated in description
        if (key === 'material') { data.material = value; continue; }
        if (key === 'country of origin') { data.countryOfOrigin = value; continue; }
        if (key === 'accessories') {
          if (value) {
            if (data.details.Accessories && data.details.Accessories !== '-') {
              data.details.Accessories += ', ' + value;
            } else {
              data.details.Accessories = value;
            }
          }
          // Always set currentSection so subsequent lines get appended
          currentSection = 'accessories';
          continue;
        }
        if (key === 'date code') { data.details['Date Code'] = value; continue; }
        if (key === 'handle length' || key === 'handle drop' || key === 'handle height' || key === 'handle') {
          // Only add to measurements if it contains actual measurements, not condition info
          if (/\d+(?:\.\d+)?\s*(?:cm|in|mm)/i.test(value)) {
            data.measurements.push(line);
          } else if (/scuff|scratch|stain|wear|dirt|crack|peel|damage|cuts|repair/i.test(value)) {
            data.condition.push('Handle: ' + value);
          }
          continue;
        }
        if (key === 'shoulder strap') { data.measurements.push('Strap: ' + value); continue; }
        if (key === 'specifications') { data.details.Features = value; continue; }
        if (key === 'features') {
          if (data.details.Features) {
            data.details.Features += '. ' + value;
          } else {
            data.details.Features = value;
          }
          continue;
        }
        if (key === 'opening/closing type' || key === 'opening/closing' || key === 'closure' || key === 'closure type') {
          // Opening/closing type goes to Features in Description
          const closureText = 'Closure: ' + value;
          if (data.details.Features) {
            data.details.Features += '. ' + closureText;
          } else {
            data.details.Features = closureText;
          }
          continue;
        }
        if (key === 'opening' || key === 'maximum opening') {
          // Check if this is a closure description vs a measurement
          if (/belt|zipper|snap|button|magnetic|turn.?lock|flap|drawstring|open$/i.test(value)) {
            // This is a closure type, add to Features
            const closureText = 'Opening: ' + value;
            if (data.details.Features) {
              data.details.Features += '. ' + closureText;
            } else {
              data.details.Features = closureText;
            }
          } else {
            // This is a measurement
            data.measurements.push('Opening: ' + value);
          }
          continue;
        }
        if (key === 'inside pocket' || key === 'inside pockets' || key === 'pockets' || key === 'inside') {
          // Pocket info goes to Pockets field
          data.details.Pockets = value;
          continue;
        }
        if (key === 'imprint') { data.details.Imprint = value; continue; }
        if (key === 'year of production' || key === 'production year') { data.details['Year Of Production'] = value; continue; }
        if (key === 'hardware') { data.details.Hardware = value; continue; }
        if (key === 'marking' || key === 'markings') { data.details.Marking = value; continue; }

        // Measurement fields that might be outside size section
        if (key === 'hips' || key === 'hip' || key === 'waist' || key === 'bust' ||
            key === 'inseam' || key === 'length' || key === 'width' || key === 'height' || key === 'depth' ||
            key === 'base length' || key === 'base width' || key === 'belt size' || key === 'belt length') {
          data.measurements.push(line);
          continue;
        }

        // Notes field - may contain measurements or be a complex section
        if (key === 'notes' || key === 'note') {
          // Check if notes contains measurements or other key:value pairs
          if (isMeasurementLine(value)) {
            data.measurements.push(value);
          } else if (/brand name:|item name:|type:|size:/i.test(value)) {
            // This is actually a run-on notes field with nested data - skip the value and parse sub-fields
            // The sub-fields should have been split by earlier processing
          } else if (value && !/^since this is clothing/i.test(value)) {
            data.notes.push(value);
          }
          continue;
        }

        // Section-specific handling
        if (currentSection === 'condition') {
          data.condition.push(line);
          continue;
        }
        if (currentSection === 'size') {
          data.measurements.push(line);
          continue;
        }
        if (currentSection === 'material') {
          if (!data.material) data.material = line;
          else data.material += ' ' + value;
          continue;
        }
        if (currentSection === 'accessories') {
          // Only append if it looks like a valid accessory item
          if (isValidAccessoryItem(value)) {
            if (data.details.Accessories && data.details.Accessories !== '-') {
              data.details.Accessories += ', ' + value;
            } else {
              data.details.Accessories = value;
            }
          } else {
            // Invalid accessory item means we've left the accessories section
            currentSection = null;
          }
          continue;
        }

        // Store as detail
        const capKey = key.charAt(0).toUpperCase() + key.slice(1);
        data.details[capKey] = value;
        continue;
      }

      // === NON-KV LINES ===

      // Handle standalone "size XX" lines (without colon) - e.g., "size 36"
      const standaloneSizeMatch = line.match(/^size\s+(\d+(?:\.\d+)?(?:\s*[A-Z])?|XXS|XS|SM|S|M|L|XL|XXL|XXXL|OS)$/i);
      if (standaloneSizeMatch && !data.sizeLabel) {
        data.sizeLabel = standaloneSizeMatch[1].trim();
        continue;
      }

      // Handle "No. X" ring size format early - common for Japanese ring sizing
      // e.g., "No. 9.5", "No.12", "No 8"
      const earlyRingNoMatch = line.match(/^No\.?\s*(\d+(?:\.\d+)?)\s*$/i);
      if (earlyRingNoMatch) {
        // Only set if sizeLabel is not already set to a meaningful value
        if (!data.sizeLabel || data.sizeLabel.trim() === '') {
          data.sizeLabel = earlyRingNoMatch[1];
        }
        // Always continue to avoid misprocessing this line
        continue;
      }

      // Check if line looks like condition content (damage/wear descriptions)
      // But NOT if it's a long descriptive sentence (product descriptions often mention "wear" casually)
      const conditionKeywords = /\b(dirt|scratch|scuff|stain|discolor|fad|crack|tear|rub|peel|tarnish|oxidat|damage|dent|chip|spot|fraying|peeling)\b/i;
      const softConditionKeywords = /\b(wear|mark|sign of use|signs of wear|minor|slight|small|light|faint)\b/i;

      // Long lines (>80 chars) that start with "This is" or similar are product descriptions, not condition
      const isProductDescription = line.length > 80 && /^(this is|this |the |a |an )/i.test(line);

      // Product descriptions go to description section FIRST - before any other checks
      // These are narrative paragraphs, not structured data
      if (isProductDescription) {
        data.description.push(line);
        continue;
      }

      // Only flag as condition if:
      // 1. Contains strong condition keywords (scratch, stain, etc.) OR
      // 2. Contains soft keywords AND is short (<80 chars) AND doesn't look like a product description
      const hasStrongConditionKeyword = conditionKeywords.test(line);
      const hasSoftConditionKeyword = softConditionKeywords.test(line) && line.length < 80;

      // Check if it's a measurement line first
      const isMeasurement = isMeasurementLine(line);

      // For measurement lines, only strong condition keywords should flag as condition
      // Soft keywords like "slight" in "Please allow for slight variations" should not flag measurements
      const looksLikeCondition = isMeasurement ? hasStrongConditionKeyword : (hasStrongConditionKeyword || hasSoftConditionKeyword);

      // Check for measurements without colon (e.g., "30cm x 26cm x 12cm")
      if (isMeasurement && currentSection !== 'condition' && !looksLikeCondition) {
        data.measurements.push(line);
        continue;
      }

      // If it looks like condition content, add to condition regardless of current section
      if (looksLikeCondition) {
        data.condition.push(line);
        continue;
      }

      // Section content
      if (currentSection === 'condition') {
        data.condition.push(line);
        continue;
      }
      if (currentSection === 'size') {
        // Check for ring size formats:
        // "No. 9.5", "No.9.5", "No 9.5", "#9.5", or just "9.5" on its own line
        const ringNoMatch = line.match(/^(?:No\.?\s*|#)?(\d+(?:\.\d+)?)\s*$/i);
        if (ringNoMatch) {
          // Only set if sizeLabel is not already set to a meaningful value
          if (!data.sizeLabel || data.sizeLabel.trim() === '') {
            data.sizeLabel = ringNoMatch[1];
          }
          // Always continue to avoid adding to measurements
          continue;
        }
        // Also handle "Ring Size: 9.5" format
        const ringSizeMatch = line.match(/^Ring\s*Size\s*[:：]\s*(\d+(?:\.\d+)?)/i);
        if (ringSizeMatch) {
          if (!data.sizeLabel || data.sizeLabel.trim() === '') {
            data.sizeLabel = ringSizeMatch[1];
          }
          continue;
        }
        // Weight lines should go to details, not measurements
        const weightMatch = line.match(/^Weight\s*:\s*(.+)$/i);
        if (weightMatch) {
          data.details.Weight = weightMatch[1].trim();
          continue;
        }
        // Only add to measurements if it actually looks like a measurement
        if (/\d/.test(line) && !looksLikeCondition) {
          data.measurements.push(line);
        }
        continue;
      }
      if (currentSection === 'material') {
        if (data.material) data.material += ' ' + line;
        else data.material = line;
        continue;
      }
      if (currentSection === 'accessories') {
        // Only append if it looks like a valid accessory item
        if (isValidAccessoryItem(line)) {
          if (data.details.Accessories && data.details.Accessories !== '-') {
            data.details.Accessories += ', ' + line;
          } else {
            data.details.Accessories = line;
          }
        } else {
          // Invalid accessory item means we've left the accessories section
          currentSection = null;
        }
        continue;
      }
      if (currentSection === 'color') {
        // Color value on next line
        if (!data.details.Color) data.details.Color = line;
        currentSection = null; // Reset after getting color
        continue;
      }
      if (currentSection === 'brand') {
        // Brand value on next line
        if (!data.details.Brand) data.details.Brand = line;
        currentSection = null;
        continue;
      }
      if (currentSection === 'category') {
        // Category value on next line
        if (!data.details.Category) data.details.Category = line;
        currentSection = null;
        continue;
      }
      if (currentSection === 'notes') {
        data.notes.push(line);
        continue;
      }

      // Authenticity mentions - but only short lines that are specifically about authenticity
      // Not long product descriptions that happen to contain "authentic"
      if (/entrupy|certificate/i.test(line) || (/authentic/i.test(line) && line.length < 60 && !/^this is/i.test(line))) {
        data.authenticity = line;
        continue;
      }

      // Ring size chart mention - convert to our format
      if (/ring size.*chart/i.test(line) || /ring size.*international/i.test(line)) {
        data.notes.push('Ring size listed is international sizing. See our Ring Size Chart for conversion.');
        continue;
      }

      // "No carat marking/s" standalone line - add to material field
      if (/no\s+(?:carat|karat)\s+marking'?s?/i.test(line)) {
        if (data.material) {
          data.material += ' / No Carat Marking';
        } else {
          data.material = 'No Carat Marking';
        }
        continue;
      }

      // Paragraph format or unknown - add to description
      if (hasParagraphFormat || !currentSection) {
        data.description.push(line);
      }
    }

    // Capitalize first letter of each word for Brand, Category, Color
    // Fixed to not capitalize after apostrophes (Women's not Women'S)
    function capitalizeWords(str) {
      if (!str) return str;
      // Capitalize after: start, space, opening parenthesis, or slash
      return str.toLowerCase().replace(/(?:^|[\s(/])\w/g, c => c.toUpperCase());
    }

    if (data.details.Brand) {
      data.details.Brand = capitalizeWords(data.details.Brand);
    }
    if (data.details.Category) {
      data.details.Category = capitalizeWords(data.details.Category);
    }
    // Merge any stray "Notation color/colour" keys into Color
    if (data.details['Notation color'] && !data.details.Color) {
      data.details.Color = data.details['Notation color'];
    }
    delete data.details['Notation color'];
    if (data.details['Notation colour'] && !data.details.Color) {
      data.details.Color = data.details['Notation colour'];
    }
    delete data.details['Notation colour'];
    if (data.details.Color) {
      let color = data.details.Color;
      // Remove "Color" word that appears in the value (e.g., "Gold Color Green" -> "Gold Green")
      color = color.replace(/\bColou?r\b/gi, '').trim();
      // Remove "System" word that sometimes appears (e.g., "Navy System" -> "Navy")
      color = color.replace(/\bSystem\b/gi, '').trim();
      // Remove "-type" suffix (e.g., "Navy-type" -> "Navy")
      color = color.replace(/-type\b/gi, '').trim();
      // Remove judgment disclaimers (e.g., "*based on our judgment")
      color = color.replace(/\s*\*?\s*based\s+on\s+our\s+judgm?ent\.?/gi, '').trim();
      // Remove subjective color disclaimers (e.g., "*Colors Are Determined Subjectively By The Seller...")
      color = color.replace(/\s*\*?\s*colou?rs?\s+are\s+determined\s+subjectively[^.]*\.?/gi, '').trim();
      color = color.replace(/\s*\*?\s*colou?rs?\s+may\s+differ\s+from[^.]*\.?/gi, '').trim();
      // Remove "(see image)" and similar parenthetical notes
      color = color.replace(/\s*\(see\s+image\s*\)/gi, '').trim();
      color = color.replace(/\s*\(refer\s+to\s+image\s*\)/gi, '').trim();
      color = color.replace(/\s*\(see\s+photo\s*\)/gi, '').trim();
      // Replace "×" separator with " / "
      color = color.replace(/\s*×\s*/g, ' / ');
      // Replace "-" between colors with " / " (e.g., "Navy-black" -> "Navy / black")
      color = color.replace(/([A-Za-z])-([A-Za-z])/g, '$1 / $2');
      // Replace " X " separator with " / " (e.g., "Black X Brown X Multi" -> "Black / Brown / Multi")
      color = color.replace(/\s+[Xx]\s+/g, ' / ');
      // Replace multiple spaces with single space
      color = color.replace(/\s{2,}/g, ' ');
      // Convert multiple colors separated by spaces to use " / " separator
      // e.g., "Gold Green" -> "Gold / Green" (but only if they look like color names)
      const commonColors = /^(black|white|brown|tan|beige|cream|grey|gray|navy|blue|red|pink|green|yellow|orange|purple|gold|silver|burgundy|maroon|cognac|camel|nude|rose|blush|ivory|chocolate|taupe|olive|teal|coral|mint|lavender|lilac|plum|bordeaux|multi|natural|khaki|bronze|copper|platinum|champagne|pearl|crystal|clear|transparent)$/i;
      const colorParts = color.split(/\s+/);
      if (colorParts.length >= 2 && colorParts.every(p => commonColors.test(p))) {
        color = colorParts.join(' / ');
      }
      // Strip trailing "and others", "and other", "etc.", "etc" (with optional comma/space before)
      color = color.replace(/\s*[,;]?\s*(and\s+others?|etc\.?)\s*$/gi, '').trim();
      // Capitalize
      color = capitalizeWords(color);
      // If color looks like gibberish (no common color words and not a brand color name), hide it
      const brandColorNames = /pomme|etoupe|galet|noir|blanc|rouge|vert|bleu|amarante|rubis|raisin|prune|cassis|figue|cuivre|cobalt|caban/i;
      if (!commonColors.test(color.toLowerCase()) &&
          !brandColorNames.test(color) &&
          !/\b(gold|silver|rose|black|white|brown|blue|red|green|yellow|pink|purple|orange|gray|grey|beige|cream|tan|navy|burgundy|multi)\b/i.test(color)) {
        // Might be gibberish or a model name - set to null
        data.details.Color = null;
      } else {
        data.details.Color = color;
      }
    }
    if (data.details.Item) {
      data.details.Item = capitalizeWords(data.details.Item);
    }

    // Capitalize authenticity text (e.g., "entrupy certificate" -> "Entrupy Certificate")
    if (data.authenticity) {
      data.authenticity = capitalizeWords(data.authenticity);
    }

    // Clean up material field - remove "None" placeholders, "Used item X" rankings, disclaimers, and capitalize
    if (data.material) {
      // Remove "Used item A/B/C/etc" - we don't use rankings
      data.material = data.material.replace(/\bUsed\s+item\s+[A-Z]\b/gi, '');

      // Remove "Used A rank" and similar condition text that got mixed in
      data.material = data.material.replace(/\bUsed\s+[A-Z]\s+rank\b.*$/gi, '');

      // Remove PC/monitor color disclaimer
      data.material = data.material.replace(/\s*The\s+color\s+may\s+look\s+different\s+depending\s+on\s+your\s+PC\/monitor\.?/gi, '');

      // Remove "items shown in images" disclaimer
      data.material = data.material.replace(/\s*The\s+items\s+shown\s+in\s+the\s+images\s+are\s+all\s+that\s+are\s+included.*$/gi, '');

      // Strip everything after "Main Item" - this is where condition text often starts
      data.material = data.material.replace(/\s*Main\s+Item\s+.*$/gi, '');

      // Strip everything after "There Is No" or "There Are No" (condition indicators)
      data.material = data.material.replace(/\s*There\s+(Is|Are)\s+No\s+.*$/gi, '');

      // Strip stamp/serial/plate descriptions
      data.material = data.material.replace(/\s*(With|Without)\s+(The\s+)?(Vca|Serial|Brand)?\s*Stamp.*$/gi, '');
      data.material = data.material.replace(/\s*No\s+(Plate|Stamp|Serial).*$/gi, '');
      data.material = data.material.replace(/\s*On\s+The\s+Back\s+Side.*$/gi, '');

      // Strip "Item", "Condition", "Details" type headers that got mixed in
      data.material = data.material.replace(/\s*\[?(Item|Condition|Details|Rank|Grade)\]?\s*:?\s*$/gi, '');

      // Remove condition details that got mixed in (e.g., "Light stains. Rubbing. Crease...")
      data.material = data.material.replace(/\s*\([^)]*(?:stain|scratch|scuff|rubbing|crease|mark|tarnish|seal)[^)]*\)/gi, '');

      // Remove "None" words (case-insensitive, whole word)
      data.material = data.material
        .replace(/\bNone\b/gi, '')
        .replace(/\s{2,}/g, ' ')  // Multiple spaces to single
        .trim();

      // Remove stray brackets like "[outer" or "[Outer" (malformed bracket format)
      data.material = data.material.replace(/\[(?:outer|inner|lining|shell|body|main|surface)\s*/gi, '');
      data.material = data.material.replace(/\[\s*/g, '');  // Remove any remaining stray opening brackets
      data.material = data.material.replace(/\s*\]/g, '');  // Remove any stray closing brackets

      // Convert periods between material names to slashes: "Wool. Nylon" → "Wool / Nylon"
      // But be careful not to affect sentences or abbreviations
      data.material = data.material.replace(/\.\s+(?=[A-Z][a-z])/g, ' / ');

      // Replace "×" separator with " / ": "Leather × Canvas" → "Leather / Canvas"
      data.material = data.material.replace(/\s*×\s*/g, ' / ');

      // Add space between numbers and words: "750white Gold" → "750 White Gold"
      data.material = data.material.replace(/(\d)([A-Za-z])/g, '$1 $2');

      // Normalize "no carat marking/s" variations to customer-friendly "No Carat Marking"
      data.material = data.material.replace(/\bno\s+(?:carat|karat)\s+marking'?s?\b/gi, 'No Carat Marking');

      // Handle line-by-line percentage format from original descriptions:
      // Original format may have:
      //   "66% rayon" (percentage first) OR "Modal 28%" (material first)
      // Expected output: "Outer: 66% Rayon / 28% Modal / 5% Silk / 1% Polyurethane / Lining: 46% Cotton / 38% Polyester / 16% Silk"
      if (/\d+%/.test(data.material)) {
        let normalized = data.material;

        // Replace ALL colons with " / " except for section headers (Outer:, Lining:, etc.)
        // First protect section headers
        normalized = normalized.replace(/(Outer|Inner|Lining|Shell|Body|Main|Surface)(\s*material)?\s*:/gi, '###$1###');
        // Replace remaining colons with slashes
        normalized = normalized.replace(/\s*:\s*/g, ' / ');
        // Restore section headers
        normalized = normalized.replace(/###(Outer|Inner|Lining|Shell|Body|Main|Surface)###/gi, '$1: ');

        // Extract all material-percentage pairs (handles BOTH formats)
        // Format 1: "66% rayon" (percentage then material)
        // Format 2: "Modal 28%" (material then percentage)
        function extractMaterialPairs(text) {
          const pairs = [];
          // Pattern 1: percentage followed by material name: "66% rayon"
          const pattern1 = /(\d+%)\s*([A-Za-z][A-Za-z]*)/gi;
          // Pattern 2: material name followed by percentage: "Modal 28%"
          const pattern2 = /([A-Za-z][A-Za-z]+)\s+(\d+%)/gi;

          let match;
          const seen = new Set();

          // Extract pattern 1 matches (percentage first)
          while ((match = pattern1.exec(text)) !== null) {
            const percent = match[1];
            const matName = match[2].trim();
            const key = percent + matName.toLowerCase();
            if (matName && !seen.has(key)) {
              seen.add(key);
              const capName = matName.charAt(0).toUpperCase() + matName.slice(1).toLowerCase();
              pairs.push({ percent, name: capName, index: match.index });
            }
          }

          // Extract pattern 2 matches (material first)
          while ((match = pattern2.exec(text)) !== null) {
            const matName = match[1].trim();
            const percent = match[2];
            const key = percent + matName.toLowerCase();
            if (matName && !seen.has(key)) {
              seen.add(key);
              const capName = matName.charAt(0).toUpperCase() + matName.slice(1).toLowerCase();
              pairs.push({ percent, name: capName, index: match.index });
            }
          }

          // Sort by position in original text to maintain order
          pairs.sort((a, b) => a.index - b.index);
          return pairs.map(p => p.percent + ' ' + p.name);
        }

        // Check for section headers like "Outer:" or "Lining:"
        const sectionRegex = /(Outer|Inner|Lining|Shell|Body|Main|Surface)\s*:\s*/gi;
        const sections = [];
        let sectionMatch;

        while ((sectionMatch = sectionRegex.exec(normalized)) !== null) {
          sections.push({
            name: sectionMatch[1],
            index: sectionMatch.index,
            endIndex: sectionMatch.index + sectionMatch[0].length
          });
        }

        const matParts = [];

        if (sections.length > 0) {
          for (let i = 0; i < sections.length; i++) {
            const section = sections[i];
            const nextSection = sections[i + 1];
            const sectionEnd = nextSection ? nextSection.index : normalized.length;
            const sectionContent = normalized.substring(section.endIndex, sectionEnd);

            const sectionPairs = extractMaterialPairs(sectionContent);

            if (sectionPairs.length > 0) {
              const sectionName = section.name.charAt(0).toUpperCase() + section.name.slice(1).toLowerCase();
              if (sections.length > 1) {
                matParts.push(sectionName + ': ' + sectionPairs.join(' / '));
              } else {
                matParts.push(...sectionPairs);
              }
            }
          }
        } else {
          // No section headers, just extract all pairs
          const pairs = extractMaterialPairs(normalized);
          matParts.push(...pairs);
        }

        if (matParts.length > 0) {
          data.material = matParts.join(' / ');
        }
      }

      // Hide "Please note that the material may be described in the item image" - no useful info
      if (data.material && /please\s+note\s+that\s+the\s+material\s+may\s+be\s+described\s+in\s+the\s+item\s+image/i.test(data.material)) {
        data.material = null;
      }

      // Hide "See Photo" / "See Image" / "As Shown In Image" materials - they provide no information
      // Also catches: "(see photo)", "refer to image", "as shown in photo/image", etc.
      if (data.material && (
          /^\s*\(?\s*(see\s+)?(photo|image)s?\s*\)?\s*\.?$/i.test(data.material.trim()) ||
          /^refer\s+to\s+(photo|image)/i.test(data.material.trim()) ||
          /^\s*\(?\s*see\s+(attached\s+)?(photo|image)s?\s*\)?\s*$/i.test(data.material.trim()) ||
          /^\s*\(?\s*as\s+shown\s+(in\s+)?(photo|image)s?\s*\)?\s*\.?$/i.test(data.material.trim()) ||
          /^\s*\(?\s*shown\s+in\s+(photo|image)s?\s*\)?\s*\.?$/i.test(data.material.trim()) ||
          /please\s+see\s+composition/i.test(data.material.trim()) ||
          /see\s+composition\s+display/i.test(data.material.trim()) ||
          /see\s+tag/i.test(data.material.trim()) ||
          /see\s+label/i.test(data.material.trim()) ||
          /check\s+label/i.test(data.material.trim()))) {
        data.material = null;
      }

      // Capitalize material names
      if (data.material) {
        if (/\d+%/.test(data.material)) {
          // For materials with percentages, capitalize material names after %
          // e.g., "100% wool" -> "100% Wool", "Main Item: 100% silk" -> "Main Item: 100% Silk"
          data.material = data.material.replace(/(\d+%\s*)([a-z]+)/gi, (match, percent, material) => {
            return percent + material.charAt(0).toUpperCase() + material.slice(1).toLowerCase();
          });
          // Also capitalize section headers like "Main Item:", "Part:", "Outer:", "Lining:"
          data.material = data.material.replace(/\b(main\s+item|part|outer|inner|lining|shell|body|surface)(\s*:)/gi, (match, section, colon) => {
            return section.split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ') + colon;
          });
        } else {
          data.material = capitalizeWords(data.material);
        }
      }

      // Convert GP/Gp/gp to Gold Plated (but not if already says "Gold Plated")
      if (data.material) {
        // Only convert standalone "GP" or "Gp" to "Gold Plated"
        data.material = data.material.replace(/\bGp\b(?!\s+Gold)/gi, 'Gold Plated');
      }

      // Add "Leather" suffix to Hermès leather names if not already present
      // Common Hermès leathers: Togo, Clemence, Taurillon, Epsom, Swift, Chevre, Barenia, Fjord, Vache, Jonathan
      // Handle compound names like "Taurillon Clemence" -> "Taurillon Clemence Leather" (not "Taurillon Leather Clemence Leather")
      if (data.material) {
        const hermesLeathers = ['Togo', 'Clemence', 'Taurillon', 'Epsom', 'Swift', 'Chevre', 'Barenia', 'Fjord', 'Vache', 'Chamonix', 'Negonda', 'Ardennes', 'Courchevel', 'Jonathan'];
        // Build regex that matches one or more consecutive leather names
        const leatherPattern = hermesLeathers.join('|');
        // Match sequence of leather names (e.g., "Taurillon Clemence" or just "Togo")
        // NOT already followed by "Leather"
        const compoundRegex = new RegExp('\\b((?:' + leatherPattern + ')(?:\\s+(?:' + leatherPattern + '))*)\\b(?!\\s+Leather)', 'gi');
        data.material = data.material.replace(compoundRegex, '$1 Leather');
      }

      // Remove "/*" and similar placeholder markers from material
      // e.g., "925 Silver/*" -> "925 Silver", "925/＊" -> "925"
      if (data.material) {
        data.material = data.material
          .replace(/\s*\/\s*[*＊\*]\s*/g, '')  // Remove /* or /＊ (fullwidth asterisk)
          .replace(/\s*[*＊\*]\s*$/g, '')       // Remove trailing * or ＊
          .trim();
      }

      // If material is now empty or just dashes, set to null
      if (!data.material || /^[\s\-]*$/.test(data.material)) {
        data.material = null;
      }
    }

    // Check if a string looks like a valid accessory item (not garbage/tags/addresses)
    function isValidAccessoryItem(text) {
      if (!text) return false;
      const trimmed = text.trim();

      // Too long - probably not an accessory name
      if (trimmed.length > 50) return false;

      // Contains underscores - likely a product tag like H3_hermes_bag
      if (/_/.test(trimmed)) return false;

      // Contains condition text
      if (/\b(condition|signs? of use|very good|excellent|good condition|no signs|item in)\b/i.test(trimmed)) return false;

      // Contains address patterns (postal codes, street numbers, building names)
      if (/\d{3,}-?\d{3,}/.test(trimmed)) return false;  // Postal codes like 104-0061
      if (/\d+-\d+-\d+/.test(trimmed)) return false;  // Address numbers like 3-7-16
      if (/\b\d+[a-z]?f\b/i.test(trimmed)) return false;  // Floor numbers like 3f
      if (/\bbuilding\b/i.test(trimmed)) return false;
      if (/\bstore\b/i.test(trimmed)) return false;
      if (/\b(tokyo|osaka|kyoto|ginza|shinjuku|shibuya)\b/i.test(trimmed)) return false;

      // Contains time patterns (store hours like 00-20:00)
      if (/\d{1,2}[:-]\d{2}/.test(trimmed)) return false;

      // Too many words - probably a sentence, not an accessory
      const wordCount = trimmed.split(/\s+/).length;
      if (wordCount > 5) return false;

      // Known valid accessory patterns
      const validPatterns = /^(dust\s*bag|box|strap|key|keys|lock|padlock|cadena|clochette|crochette|pouch|mirror|cards?|receipt|invoice|certificate|charm|twilly|scarf|ribbon|rain\s*cover|shoulder\s*strap|name\s*tag|luggage\s*tag|wallet|coin\s*purse|insert|organizer|hook|carabiner|soft\s*case|hard\s*case|repair\s*certificate|authenticity\s*card|care\s*card|booklet|manual)s?$/i;
      if (validPatterns.test(trimmed)) return true;

      // Allow short items (1-3 words) that don't match garbage patterns
      if (wordCount <= 3 && /^[A-Za-z\s]+$/.test(trimmed)) return true;

      return false;
    }

    // Normalize accessory terms (e.g., "Storage bag" -> "Dust Bag")
    function normalizeAccessoryTerms(text) {
      if (!text) return text;
      // Patterns for various bag terms that should become "Dust Bag"
      return text
        .replace(/\b(genuine\s+)?protective\s+bag\b/gi, 'Dust Bag')
        .replace(/\bstorage\s+bag\b/gi, 'Dust Bag')
        .replace(/\bsleeper\s+bag\b/gi, 'Dust Bag')
        .replace(/\bsleeper\b/gi, 'Dust Bag')
        .replace(/\bprotector\s+bag\b/gi, 'Dust Bag')
        .replace(/\bprotection\s+bag\b/gi, 'Dust Bag')
        .replace(/\bcover\s+bag\b/gi, 'Dust Bag')
        .replace(/\bcloth\s+bag\b/gi, 'Dust Bag')
        .replace(/\bdrawstring\s+bag\b/gi, 'Dust Bag')
        .replace(/\bflannel\s+bag\b/gi, 'Dust Bag')
        .replace(/\binner\s+bag\b/gi, 'Dust Bag')
        .replace(/\bbag\s+cover\b/gi, 'Dust Bag')
        // Normalize BOX variations
        .replace(/\b(genuine\s+)?box\b/gi, 'Box')
        // Remove "Genuine" prefix from other items
        .replace(/\bgenuine\s+/gi, '')
        // Remove "Comes With" prefix (e.g., "Comes With Repair Certificate" -> "Repair Certificate")
        .replace(/\bcomes\s+with\s+/gi, '')
        // Remove standalone "With" prefix (e.g., "With Box" -> "Box")
        .replace(/\bwith\s+/gi, '');
    }

    // Clean up Accessories field - remove disclaimers and normalize terms
    if (data.details.Accessories) {
      data.details.Accessories = data.details.Accessories
        // Remove condition ranks that leaked into accessories
        // e.g., "Rank: Sa", "Rank: AB", "Rank : B+"
        .replace(/\s*Rank\s*:\s*[A-Za-z+\-]+\s*/gi, '')
        // Remove freeform description content that leaked into accessories
        // e.g., "None Details: Hardware mark..." -> "None"
        .replace(/\s*(Details?|Comments?|About\s+Us)\s*[：:\-].*$/i, '')
        .replace(/-?\s*please\s+note\s+that\s+the\s+item\s+is\s+pre-owned.*$/i, '')
        .replace(/-?\s*please\s+note\s+that.*$/i, '')
        .replace(/\s*there are no accessories other than what is listed\.?/i, '')  // Remove this disclaimer
        .replace(/\s*everything\s+shown\s+in\s+the\s+photo\s+is\s+included\.?/i, '')  // Remove this useless phrase
        .replace(/\s*\*?\s*accessories\s+may\s+have\s+scratches[^,]*(?:,\s*[^,]+)*\.?/gi, '')  // Remove "*Accessories may have scratches. Dirt. Or deterioration."
        .replace(/\s*\([^)]*\)/g, '')  // Remove parenthetical content like "(Please Consider It As A Bonus...)"
        .replace(/\s{2,}/g, ' ')
        .trim();

      // Hide useless accessory values - these give no useful information
      if (/^(main\s+item\s+only|no\s+accessories|none|n\/a)\.?$/i.test(data.details.Accessories.trim())) {
        data.details.Accessories = '-';
      }

      // Normalize accessory terms
      data.details.Accessories = normalizeAccessoryTerms(data.details.Accessories);

      // Split accessories properly - handle "Box. bag" or "Soft case repair certificate" patterns
      // First, normalize separators: periods followed by space or lowercase = new item
      let accText = data.details.Accessories;
      // Split on periods that separate items (e.g., "Box. bag" -> ["Box", "bag"])
      accText = accText.replace(/\.\s+(?=[a-zA-Z])/g, ', ');
      // Split on multiple spaces that might separate items
      accText = accText.replace(/\s{2,}/g, ', ');

      // Now split into individual items, clean up and capitalize each
      const accItems = accText.split(/\s*,\s*/).map(item => item.trim()).filter(item => item.length > 0);
      const cleanedItems = [];

      for (let item of accItems) {
        // Capitalize each word
        item = item.replace(/\b\w+/g, word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
        // Normalize terms after capitalizing
        item = normalizeAccessoryTerms(item);
        if (item && !cleanedItems.includes(item)) {
          cleanedItems.push(item);
        }
      }

      data.details.Accessories = cleanedItems.join(', ');

      // If just a dash or empty, keep as "-" to indicate none included
      if (!data.details.Accessories || /^[\s]*$/.test(data.details.Accessories)) {
        data.details.Accessories = '-';
      }
    }

    // Clean up Features field - hide if just dashes or empty-looking
    if (data.details.Features) {
      // If Features is just dashes, dots, or whitespace, hide it
      if (/^[\s\-–—\.·]+$/.test(data.details.Features.trim())) {
        data.details.Features = null;
      }
    }

    // Move accessory-like entries from condition to Accessories
    // Look for entries like "With cadena. with key. with crochette. with storage bag"
    if (data.condition.length > 0) {
      const accessoryKeywords = /\b(cadena|padlock|lock|key|clochette|crochette|dust\s*bag|storage\s*bag|box|strap|charm|pouch|mirror|cards?|receipt|invoice|sleeper|rain\s*cover)\b/i;
      const conditionKeywords = /\b(scratch|stain|wear|dirt|mark|fad|crack|tear|rub|peel|tarnish|oxidat|damage|dent|chip|spot|discolor|fraying|peeling|scuff)\b/i;

      const newCondition = [];
      for (const entry of data.condition) {
        // Check if entry starts with "With" and contains accessory keywords
        if (/^with\s+/i.test(entry) && accessoryKeywords.test(entry) && !conditionKeywords.test(entry)) {
          // This is an accessory line, move it
          let accessoryText = entry
            .replace(/^with\s+/i, '')
            .replace(/\.\s*with\s+/gi, ', ')  // "with cadena. with key" -> "cadena, key"
            .replace(/\.\s*$/g, '')  // Remove trailing period
            .trim();
          // Capitalize each accessory item
          accessoryText = accessoryText.replace(/\b\w/g, c => c.toUpperCase());
          // Normalize terms (e.g., "Storage Bag" -> "Dust Bag")
          accessoryText = normalizeAccessoryTerms(accessoryText);
          if (data.details.Accessories && data.details.Accessories !== '-') {
            data.details.Accessories += ', ' + accessoryText;
          } else {
            data.details.Accessories = accessoryText;
          }
        } else {
          newCondition.push(entry);
        }
      }
      data.condition = newCondition;
    }

    // Clean up stamp markers from condition (e.g., "(O stamp)")
    data.condition = data.condition.filter(c => !/^\([A-Z]\s*stamp\)$/i.test(c.trim()));

    // Move pocket descriptions from condition to Features
    const pocketPattern = /\b(\d+\s*)?(open\s+)?pocket|zipper\s+pocket|card\s+slot|compartment/i;
    const finalCondition = [];
    for (const entry of data.condition) {
      if (pocketPattern.test(entry) && !/scratch|stain|wear|dirt|damage/i.test(entry)) {
        // This is a pocket/compartment description, move to Features
        let pocketText = entry.trim();
        // Capitalize first letter
        pocketText = pocketText.charAt(0).toUpperCase() + pocketText.slice(1);
        if (data.details.Features) {
          data.details.Features += '. ' + pocketText;
        } else {
          data.details.Features = pocketText;
        }
      } else {
        finalCondition.push(entry);
      }
    }
    data.condition = finalCondition;

    // Move misplaced Color/Material entries from condition to proper sections
    // Patterns like "●Color" followed by color values, or "Lining: 46% cotton..."
    const colorMaterialCondition = [];
    let pendingColorValues = false;
    let pendingMaterialValues = false;
    for (const entry of data.condition) {
      const trimmed = entry.trim();
      // Check for color header
      if (/^●?\s*Color\s*$/i.test(trimmed)) {
        pendingColorValues = true;
        continue;
      }
      // Check for material header
      if (/^●?\s*Material\s*$/i.test(trimmed) || /^Outer$/i.test(trimmed)) {
        pendingMaterialValues = true;
        continue;
      }
      // If we're expecting color values (short entries that look like colors)
      if (pendingColorValues && trimmed.length < 30 && /^[A-Za-z\s\/]+$/.test(trimmed)) {
        if (!data.details.Color) {
          data.details.Color = trimmed;
        } else {
          data.details.Color += ' / ' + trimmed;
        }
        continue;
      } else if (pendingColorValues) {
        pendingColorValues = false;
      }
      // If we're expecting material values (has % or common material words)
      if (pendingMaterialValues && (/\d+%/.test(trimmed) || /lining|cotton|silk|wool|polyester|nylon|leather|canvas/i.test(trimmed))) {
        if (!data.material) {
          data.material = trimmed;
        } else {
          data.material += ' / ' + trimmed;
        }
        continue;
      } else if (pendingMaterialValues && !/^●/.test(trimmed)) {
        pendingMaterialValues = false;
      }
      // Extract material lines that got stuck in condition
      if (/^Lining\s*:/i.test(trimmed) || /^\d+%\s*\w+/i.test(trimmed)) {
        if (!data.material) {
          data.material = trimmed.replace(/^Lining\s*:\s*/i, 'Lining: ');
        } else {
          data.material += ' / ' + trimmed.replace(/^Lining\s*:\s*/i, 'Lining: ');
        }
        continue;
      }
      colorMaterialCondition.push(entry);
    }
    data.condition = colorMaterialCondition;

    // Move misplaced detail fields from condition to proper sections
    // These are fields like "Model Name:", "Series:", etc. that got into condition
    const detailFieldsCondition = [];
    for (const entry of data.condition) {
      const trimmed = entry.trim();
      // Check for detail field patterns
      const modelMatch = trimmed.match(/^Model\s*(?:Name)?\s*:\s*(.+)$/i);
      if (modelMatch && !data.details['Model Name']) {
        data.details['Model Name'] = modelMatch[1].trim();
        continue;
      }
      const seriesMatch = trimmed.match(/^(?:Series|Line)\s*:\s*(.+)$/i);
      if (seriesMatch && !data.details.Series) {
        data.details.Series = seriesMatch[1].trim();
        continue;
      }
      const brandMatch = trimmed.match(/^Brand\s*:\s*(.+)$/i);
      if (brandMatch && !data.details.Brand) {
        data.details.Brand = brandMatch[1].trim();
        continue;
      }
      const categoryMatch = trimmed.match(/^(?:Category|Type)\s*:\s*(.+)$/i);
      if (categoryMatch && !data.details.Category) {
        data.details.Category = categoryMatch[1].trim();
        continue;
      }
      const colorMatch = trimmed.match(/^Colou?r\s*:\s*(.+)$/i);
      if (colorMatch && !data.details.Color) {
        data.details.Color = colorMatch[1].trim();
        continue;
      }
      const hardwareColorMatch = trimmed.match(/^Hardware\s*Colou?r\s*:\s*(.+)$/i);
      if (hardwareColorMatch && !data.details['Hardware Color']) {
        data.details['Hardware Color'] = hardwareColorMatch[1].trim();
        continue;
      }
      const hardwareMatch = trimmed.match(/^Hardware\s*:\s*(.+)$/i);
      if (hardwareMatch && !data.details.Hardware) {
        data.details.Hardware = hardwareMatch[1].trim();
        continue;
      }
      const accessoriesMatch = trimmed.match(/^Accessories\s*:\s*(.+)$/i);
      if (accessoriesMatch && !data.details.Accessories) {
        data.details.Accessories = accessoriesMatch[1].trim();
        continue;
      }
      detailFieldsCondition.push(entry);
    }
    data.condition = detailFieldsCondition;

    // Remove duplicates from all arrays
    function removeDuplicates(arr) {
      const seen = new Set();
      return arr.filter(item => {
        const normalized = item.toLowerCase().trim();
        if (seen.has(normalized)) return false;
        seen.add(normalized);
        return true;
      });
    }

    data.condition = removeDuplicates(data.condition);
    data.measurements = removeDuplicates(data.measurements);
    data.description = removeDuplicates(data.description);
    data.notes = removeDuplicates(data.notes);

    // Filter out marketing fluff and empty lines from description
    const FLUFF_PATTERNS = [
      /^this is a [a-z]+/i,  // "This is a Gucci..." marketing intros
      /^made of high.quality/i,
      /^it has a soft texture/i,
      /^be sure to use it/i,
      /^recommended scenes?\s*-?/i,
      /^the calm .* color/i,
      /^due to the universal design/i,
      /^it is perfect for/i,
      /^will brighten up/i,
      /^gently protects your/i,
      /^as a focal point/i,
      /^goes (very )?well with/i,
      /^perfect for pairing/i,
      /^a great gift/i,
      /^regardless of gender/i,
      /^\*?this is a used item/i,
      /^please note that since we/i,
      /^please check the posted photos/i,
      /^we are unable to fully grasp/i,
      /^although there are some/i,
      /^this item has little signs/i,
      /^and is in good condition/i,
      /^material see image/i,
      /^see image\s*:?\s*$/i,
      /^-?\s*notation size\s/i,  // Move to Size section, not Description
      /^-?\s*accessories\s*$/i,  // Empty accessories header
      /^-?\s*$/,  // Empty or just dashes
      /^condition\s*-?\s*$/i,  // "Condition-" header with nothing
      /^\s*-\s*$/,  // Just a dash
    ];

    data.description = data.description.filter(line => {
      const trimmed = line.trim();
      // Filter empty lines
      if (!trimmed || trimmed.length < 3) return false;
      // Filter lines that are just punctuation/dashes
      if (/^[\s\-\.\,\:]+$/.test(trimmed)) return false;
      // Filter marketing fluff
      if (FLUFF_PATTERNS.some(p => p.test(trimmed))) return false;
      // Filter lines that match main SKIP_PATTERNS
      if (SKIP_PATTERNS.some(p => p.test(trimmed))) return false;
      return true;
    });

    // Also filter notes
    data.notes = data.notes.filter(line => {
      const trimmed = line.trim();
      if (!trimmed || trimmed.length < 3) return false;
      if (/^[\s\-\.\,\:]+$/.test(trimmed)) return false;
      if (SKIP_PATTERNS.some(p => p.test(trimmed))) return false;
      return true;
    });

    // === MERGE DIRECT MEASUREMENTS ===
    // Direct measurements are clean - use them to REPLACE any dirty line-parsed values
    if (directMeasurements && directMeasurements.length > 0) {
      // Build a map of clean measurements by key
      const cleanMeasurements = new Map();
      for (const dm of directMeasurements) {
        const match = dm.match(/^([^:]+):/);
        const key = match ? match[1].toLowerCase().trim() : dm.toLowerCase();
        cleanMeasurements.set(key, dm);
      }

      // Replace dirty values with clean ones, keep non-duplicates
      const seen = new Set();
      const finalMeasurements = [];

      // First add all clean measurements
      for (const [key, value] of cleanMeasurements) {
        finalMeasurements.push(value);
        seen.add(key);
      }

      // Then add line-parsed measurements that weren't in direct extraction
      for (const m of data.measurements) {
        const match = m.match(/^([^:]+):/);
        const key = match ? match[1].toLowerCase().trim() : m.toLowerCase();
        if (!seen.has(key)) {
          finalMeasurements.push(m);
          seen.add(key);
        }
      }

      data.measurements = finalMeasurements;
    }

    // === FINAL CLEANUP PASS ===
    // This runs AFTER all data has been collected from various sources
    // to ensure consistent formatting

    // Final Color cleanup - capitalize and format
    if (data.details.Color) {
      let color = data.details.Color;
      // Hide "see photo/image" colors - they provide no information
      if (/^\s*(see\s+)?(photo|image)s?\s*$/i.test(color) ||
          /^\s*\(?\s*(see\s+)?(photo|image)s?\s*\)?\s*$/i.test(color) ||
          /^refer\s+to\s+(photo|image)/i.test(color)) {
        data.details.Color = null;
      } else {
        // Remove "Type", "System", "-type" and similar words
        color = color.replace(/\bType\b/gi, '');
        color = color.replace(/\bSystem\b/gi, '');
        color = color.replace(/-type\b/gi, '');
        // Remove subjective color disclaimers
        color = color.replace(/\s*\*?\s*colou?rs?\s+are\s+determined\s+subjectively[^.]*\.?/gi, '').trim();
        color = color.replace(/\s*\*?\s*colou?rs?\s+may\s+differ\s+from[^.]*\.?/gi, '').trim();
        // Add space before opening parenthesis when preceded by letter: "Etoupe(Etoupe)" -> "Etoupe (Etoupe)"
        color = color.replace(/([A-Za-z])(\()/g, '$1 $2');
      // Replace separators with " / "
      color = color.replace(/\s*[×:]\s*/g, ' / ');
      color = color.replace(/([A-Za-z])-([A-Za-z])/g, '$1 / $2');
      color = color.replace(/\s+[Xx]\s+/g, ' / ');
      // Remove trailing/leading dashes from color parts (e.g., "Navy-" -> "Navy")
      color = color.replace(/-\s*\//g, ' /');  // "Navy- /" -> "Navy /"
      color = color.replace(/\/\s*-/g, '/ ');  // "/ -Black" -> "/ Black"
      color = color.replace(/^-+\s*/, '');     // Leading dashes
      color = color.replace(/\s*-+$/, '');     // Trailing dashes
      // Clean up extra spaces before capitalizing
      color = color.replace(/\s{2,}/g, ' ').trim();
      // Detect multiple color words separated by spaces and add " / " between them
      // e.g., "Black Purple" -> "Black / Purple", "Navy White Red" -> "Navy / White / Red"
      const commonColorWords = /^(black|white|brown|tan|beige|cream|grey|gray|navy|blue|red|pink|green|yellow|orange|purple|gold|silver|burgundy|maroon|cognac|camel|nude|rose|blush|ivory|chocolate|taupe|olive|teal|coral|mint|lavender|lilac|plum|bordeaux|multi|natural|khaki|bronze|copper|platinum|champagne|pearl|crystal|clear|transparent|ecru|noir|blanc|rouge|vert|bleu|etoupe|gris|jaune|violet|turquoise|magenta|indigo|charcoal|slate|sand|mocha|espresso|rust|wine|emerald|sapphire|ruby|amber|ochre|mustard|salmon|peach|apricot|mauve|fuchsia|cyan|lime|aqua|periwinkle|scarlet|crimson|vermilion|cerulean|cobalt|azure|midnight|forest|hunter|sage|seafoam|dusty|bold)$/i;
      if (!color.includes('/')) {
        const words = color.split(/\s+/);
        if (words.length >= 2 && words.every(w => commonColorWords.test(w))) {
          color = words.join(' / ');
        }
      }
      // Split, deduplicate, capitalize each word
      const colorParts = color.split(/\s*\/\s*/).map(part => {
        return part.trim().toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
      }).filter(part => part.length > 0);
      // Deduplicate colors (case-insensitive)
      const seenColors = new Set();
      const uniqueColors = [];
      for (const c of colorParts) {
        const lower = c.toLowerCase();
        if (!seenColors.has(lower)) {
          seenColors.add(lower);
          uniqueColors.push(c);
        }
      }
      color = uniqueColors.join(' / ');
      // Clean up any remaining issues
      color = color.replace(/\s{2,}/g, ' ').trim();
      // Remove empty parts from splits (e.g., "Navy /  / Black" -> "Navy / Black")
      color = color.replace(/\s*\/\s*\/\s*/g, ' / ');
      data.details.Color = color;
      }
    }

    // Final Hardware Color cleanup
    if (data.details['Hardware Color']) {
      let hwColor = data.details['Hardware Color'];
      // Remove "-Hardware" suffix (e.g., "Silver-Hardware" -> "Silver")
      hwColor = hwColor.replace(/-?\s*Hardware\s*$/i, '').trim();
      // Capitalize first letter
      hwColor = hwColor.charAt(0).toUpperCase() + hwColor.slice(1).toLowerCase();
      data.details['Hardware Color'] = hwColor;
    }

    // Final Material cleanup - replace colons with slashes and capitalize
    if (data.material) {
      let mat = data.material;
      // Protect section headers (Outer:, Lining:, Main Item:, Part:, etc.)
      mat = mat.replace(/(Outer|Inner|Lining|Shell|Body|Main\s*Item|Main|Surface|Part)(\s*material)?\s*:/gi, (m, section) => {
        return '###' + section.replace(/\s+/g, '_') + '###';
      });
      // Replace colons between materials with slashes
      mat = mat.replace(/\s*:\s*/g, ' / ');
      // Restore section headers with proper capitalization
      mat = mat.replace(/###([A-Za-z_]+)###/gi, (m, section) => {
        return section.replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ') + ': ';
      });
      // Capitalize material names after percentages (handles both "100% wool" and "100% Wool")
      mat = mat.replace(/(\d+%\s*)([a-zA-Z]+)/g, (m, percent, material) => {
        return percent + material.charAt(0).toUpperCase() + material.slice(1).toLowerCase();
      });
      // Clean up double spaces and slashes
      mat = mat.replace(/\s{2,}/g, ' ').replace(/\s*\/\s*\/\s*/g, ' / ').trim();
      data.material = mat;
    }

    return data;
  }

  /**
   * Format measurements for display
   */
  function formatMeasurements(measurements) {
    if (!measurements || measurements.length === 0) return '';

    // Pre-processing: Normalize labels and terms
    measurements = measurements.map(m => {
      // Clean ". " (dot-space) before numbers in measurement values
      // e.g., "Handle Length: . 33 cm" -> "Handle Length: 33 cm"
      // e.g., "Size: . 90 cm" -> "Size: 90 cm"
      m = m.replace(/:\s*\.\s+(\d)/g, ': $1');
      // Clean dot-space after W/H/D dimension letters (anywhere in string, not just start)
      // e.g., "W . 31 cm x H . 24 cm x D . 12 cm" -> "W 31 cm x H 24 cm x D 12 cm"
      m = m.replace(/\b([WHD])\s*\.\s+(\d)/gi, '$1 $2');
      // Normalize "W " / "H " / "D " (single letter dimensions) to full words
      // Handles "W 31 cm x H 24 cm x D 12 cm" and standalone "W: 31 cm"
      m = m.replace(/^W\s*(?=\d|:)/i, 'Width ');
      m = m.replace(/^H\s*(?=\d|:)/i, 'Height ');
      m = m.replace(/^D\s*(?=\d|:)/i, 'Depth ');
      // Also expand W/H/D in combined dimension strings: "W 31 cm x H 24 cm x D 12 cm"
      m = m.replace(/\bW\s*(?=\d)/g, 'Width ');
      m = m.replace(/\bH\s*(?=\d)/g, 'Height ');
      m = m.replace(/\bD\s*(?=\d)/g, 'Depth ');
      // Normalize "W:" to "Width:" and "H:" to "Height:" at the start of lines
      m = m.replace(/^W\s*:\s*/i, 'Width: ');
      m = m.replace(/^H\s*:\s*/i, 'Height: ');
      // Normalize all variants of "approximately" and "approx" to "Approx." (with capital A)
      m = m.replace(/\bApproximately\b/gi, 'Approx.');
      m = m.replace(/\bapprox\.?/gi, 'Approx.');
      // Remove disclaimer text like "(Please allow for slight variations)"
      m = m.replace(/\s*\(?\s*please\s+allow\s+for\s+slight\s+variations?\s*\)?\.?/gi, '');
      // Add space between numbers and units: "101-111cm" -> "101-111 cm", "23cm" -> "23 cm"
      m = m.replace(/(\d+(?:\.\d+)?|\d+-\d+(?:\.\d+)?)(cm|in|mm)\b/gi, '$1 $2');
      return m;
    });

    // Simple cleanup pass - remove garbage from measurement strings
    measurements = measurements.map(m => {
      // FIRST: Check if this is a compact w:/h:/d: dimension format that needs preservation
      // e.g., "( Approx. )w: 20.5 cm X h: 13 cm X d: 2 cm" or "Size: ( Approx. )w: 20.5 cm..."
      // These should NOT be split at the first colon - they need to go through the expansion loop intact
      const isCompactWHD = /w\s*:\s*\d+(?:\.\d+)?\s*(?:cm|in|mm)\s*(?:\([^)]+\))?\s*[xX×]\s*h\s*:/i.test(m);
      if (isCompactWHD) {
        // Just clean up and return - don't truncate
        return m.replace(/\s*\[[^\]]*\]\s*/g, ' ')  // Remove bracket content
                .replace(/\s*\[[a-z\s]+:/gi, ' ')   // Remove malformed brackets
                .replace(/["'""''″′]+\s*/g, ' ')    // Remove trailing quotes
                .replace(/\s{2,}/g, ' ')            // Clean up double spaces
                .trim();
      }

      // If there's a colon (Label: value format), extract just the first measurement value
      const colonIdx = m.indexOf(':');
      if (colonIdx > 0) {
        const label = m.slice(0, colonIdx).trim();
        let value = m.slice(colonIdx + 1).trim();

        // FIRST: Convert inch values (with " or ') to cm format BEFORE any other processing
        // e.g., "1.37"" or "1.37'" -> "3.48cm (1.37in)"
        const inchMatch = value.match(/^(\d+(?:\.\d+)?)\s*["'″′"']+\s*$/);
        if (inchMatch) {
          const inchVal = parseFloat(inchMatch[1]);
          const cmVal = (inchVal * 2.54).toFixed(2);
          value = cmVal + ' cm (' + inchVal.toFixed(2) + ' in)';
        }

        // DON'T truncate dimension format lines like "30cm (11.81in)x22cm (8.66in)x16cm"
        // Also preserve "w: 20.5cm X h: 13cm X d: 2cm" format for expansion below
        // These need to be preserved for the expansion loop below
        const isDimensionFormat = /\d+\s*cm\s*(?:\([^)]+\))?\s*[xX×]\s*\d+/i.test(value) ||
                                  /w\s*:\s*.+\s*x\s*h\s*:\s*.+\s*x\s*d\s*:/i.test(value);
        if (!isDimensionFormat) {
          // Extract first measurement: number + unit + optional (inches)
          const firstMeasure = value.match(/^(\d+(?:\.\d+)?\s*(?:cm|in|mm)(?:\s*\([^)]+\))?)/i);
          if (firstMeasure) {
            value = firstMeasure[1];
          }
        }
        m = label + ': ' + value;
      }
      // Remove bracket content like [Others], [See image]
      m = m.replace(/\s*\[[^\]]*\]\s*/g, ' ');
      // Remove malformed brackets like [label:
      m = m.replace(/\s*\[[a-z\s]+:/gi, ' ');
      // Remove trailing quotes (before or after other garbage)
      m = m.replace(/["'""''″′]+\s*/g, ' ');
      // Remove dash-prefixed garbage like "-Material", "-Color" that got stuck
      m = m.replace(/\s*-\s*(Material|Color|Condition|Accessories|Notes?|Remarks?|Others).*$/gi, '');
      // Remove "Size-" prefix from measurement labels (e.g., "Size-Total Length:" -> "Total Length:")
      m = m.replace(/^Size-/i, '');
      // Clean up double spaces
      m = m.replace(/\s{2,}/g, ' ');
      return m.trim();
    }).filter(m => {
      // Must have content and digits
      if (m.length === 0 || !/\d/.test(m)) return false;
      // Must have measurement units (cm, in, mm, ") OR be in "Label: value" format
      // This filters out random product codes like "8004251115096t"
      const hasMeasurementUnits = /\d+(?:\.\d+)?\s*(?:cm|in|mm|"|')\b/i.test(m);
      const hasLabelFormat = /^[A-Za-z][A-Za-z\s]*:\s*\d/.test(m);
      return hasMeasurementUnits || hasLabelFormat;
    });

    // Expand compact dimension formats and normalize
    const expanded = [];
    for (let m of measurements) {
      // Handle compact format: "w:15.5cm (6.10in)(bottom) x h:11cm (4.33in) x d:4.5cm (1.77in)"
      // Also handles: "( Approx. )w: 20.5cm ( 8.07in ) X h: 13cm ( 5.12in ) X d: 2cm ( 0.79in )"
      // Also handles: "Size: ( Approx. )w: 20.5cm ( 8.07in ) X h: 13cm ( 5.12in ) X d: 2cm ( 0.79in )"
      // Convert to separate lines: "Width: 15.5cm (6.10in) (bottom)", "Height: 11cm (4.33in)", "Depth: 4.5cm (1.77in)"
      const compactMatch = m.match(/^(?:Size\s*:\s*)?(?:\(\s*Approx\.?\s*\)\s*)?w\s*:\s*(.+?)\s*x\s*h\s*:\s*(.+?)\s*x\s*d\s*:\s*(.+)$/i);
      if (compactMatch) {
        let width = compactMatch[1].trim();
        let height = compactMatch[2].trim();
        let depth = compactMatch[3].trim();
        // Clean up extra parenthetical notes like "(bottom)"
        width = width.replace(/\(bottom\)/i, '').trim();
        expanded.push('Width: ' + width);
        expanded.push('Height: ' + height);
        expanded.push('Depth: ' + depth);
        continue;
      }

      // Handle compact format without colons: "W28cm (11.02in) x H16cm (6.30in) x D10cm (3.94in)"
      // Also handles: "W28cm x H16cm x D10cm" (without inches)
      // Also handles: "W19cm (7.48in)×H10cm (3.94in)×D2.5cm (0.98in)" (with × separator)
      const compactNoColonMatch = m.match(/^W\s*(\d+(?:\.\d+)?)\s*cm\s*(?:\([^)]+\))?\s*[xX×]\s*H\s*(\d+(?:\.\d+)?)\s*cm\s*(?:\([^)]+\))?\s*[xX×]\s*D\s*(\d+(?:\.\d+)?)\s*cm\s*(?:\([^)]+\))?$/i);
      if (compactNoColonMatch) {
        const wVal = compactNoColonMatch[1];
        const hVal = compactNoColonMatch[2];
        const dVal = compactNoColonMatch[3];
        const wIn = (parseFloat(wVal) / 2.54).toFixed(2);
        const hIn = (parseFloat(hVal) / 2.54).toFixed(2);
        const dIn = (parseFloat(dVal) / 2.54).toFixed(2);
        expanded.push(`Width: ${wVal} cm (${wIn} in)`);
        expanded.push(`Height: ${hVal} cm (${hIn} in)`);
        expanded.push(`Depth: ${dVal} cm (${dIn} in)`);
        continue;
      }

      // Handle compact format with cm only after D: "Approx. W30 x H22.5 x D9 cm (3.54 in)"
      // This is different from above pattern - here cm only appears once at the end
      const compactCmEndMatch = m.match(/^(?:Approx\.?\s*)?W\s*(\d+(?:\.\d+)?)\s*[xX×]\s*H\s*(\d+(?:\.\d+)?)\s*[xX×]\s*D\s*(\d+(?:\.\d+)?)\s*cm\s*(?:\([^)]+\))?$/i);
      if (compactCmEndMatch) {
        const wVal = compactCmEndMatch[1];
        const hVal = compactCmEndMatch[2];
        const dVal = compactCmEndMatch[3];
        const wIn = (parseFloat(wVal) / 2.54).toFixed(2);
        const hIn = (parseFloat(hVal) / 2.54).toFixed(2);
        const dIn = (parseFloat(dVal) / 2.54).toFixed(2);
        expanded.push(`Width: ${wVal} cm (${wIn} in)`);
        expanded.push(`Height: ${hVal} cm (${hIn} in)`);
        expanded.push(`Depth: ${dVal} cm (${dIn} in)`);
        continue;
      }

      // Handle "W approx. 30cm (11.81in) x H approx. 23cm (9.06in) x D approx. 8cm (3.15in)" format
      // Also handles "W Approx. 30 cm (11.81 in) x H Approx. 23 cm (9.06 in) x D Approx. 8 cm (3.15 in)"
      const whdApproxMatch = m.match(/W\s*(?:approx\.?\s*)?(\d+(?:\.\d+)?)\s*cm\s*(?:\(\s*[\d.]+\s*in\s*\))?\s*[xX×]\s*H\s*(?:approx\.?\s*)?(\d+(?:\.\d+)?)\s*cm\s*(?:\(\s*[\d.]+\s*in\s*\))?\s*[xX×]\s*D\s*(?:approx\.?\s*)?(\d+(?:\.\d+)?)\s*cm/i);
      if (whdApproxMatch) {
        const wVal = whdApproxMatch[1];
        const hVal = whdApproxMatch[2];
        const dVal = whdApproxMatch[3];
        const wIn = (parseFloat(wVal) / 2.54).toFixed(2);
        const hIn = (parseFloat(hVal) / 2.54).toFixed(2);
        const dIn = (parseFloat(dVal) / 2.54).toFixed(2);
        expanded.push(`Width: ${wVal} cm (${wIn} in)`);
        expanded.push(`Height: ${hVal} cm (${hIn} in)`);
        expanded.push(`Depth: ${dVal} cm (${dIn} in)`);
        continue;
      }

      // Handle "Dimensions: 30cm (11.81in)x22cm (8.66in)x16cm (6.30in)" format
      // OR "Width x Height x Width (Depth): 30cm (11.81in)x22cm (8.66in)x16cm (6.30in)"
      // Values are separated by "x" (or X) without spaces between the inch value and separator
      // Pattern: Label: NNcm (NN.NNin)xNNcm (NN.NNin)xNNcm (NN.NNin)
      // Handle "Size (height x width x depth): Approx. 22.5cm x 30.5cm x 8cm" format
      // Also handles "Size (h x w x d):" format
      const sizeHWDMatch = m.match(/^Size\s*\(\s*(?:height|h)\s*[xX×]\s*(?:width|w)\s*[xX×]\s*(?:depth|d)\s*\)\s*:\s*(?:Approx\.?\s*)?(.+)$/i);
      if (sizeHWDMatch) {
        const valuesStr = sizeHWDMatch[1].trim();
        // Extract three dimension values: 22.5cm x 30.5cm x 8cm (optionally with inch values)
        const valueMatch = valuesStr.match(/(\d+(?:\.\d+)?)\s*cm\s*(?:\([^)]+\))?\s*[xX×]\s*(\d+(?:\.\d+)?)\s*cm\s*(?:\([^)]+\))?\s*[xX×]\s*(\d+(?:\.\d+)?)\s*cm/i);
        if (valueMatch) {
          const hVal = valueMatch[1];
          const wVal = valueMatch[2];
          const dVal = valueMatch[3];
          const hIn = (parseFloat(hVal) / 2.54).toFixed(2);
          const wIn = (parseFloat(wVal) / 2.54).toFixed(2);
          const dIn = (parseFloat(dVal) / 2.54).toFixed(2);
          expanded.push(`Height: ${hVal} cm (${hIn} in)`);
          expanded.push(`Width: ${wVal} cm (${wIn} in)`);
          expanded.push(`Depth: ${dVal} cm (${dIn} in)`);
          continue;
        }
      }

      const dimensionsLabelMatch = m.match(/^(?:Dimensions|Width\s*[xX×]\s*Height\s*[xX×]\s*(?:Width\s*)?\(?Depth\)?)\s*:\s*(.+)$/i);
      if (dimensionsLabelMatch) {
        const valuesStr = dimensionsLabelMatch[1].trim();
        // Extract three dimension values from format: 30cm (11.81in)x22cm (8.66in)x16cm (6.30in)
        // Split on "x" or "X" that appears after a closing paren or after "cm"/"in"
        const valueMatch = valuesStr.match(/(\d+(?:\.\d+)?)\s*cm\s*(?:\([^)]+\))?\s*[xX×]\s*(\d+(?:\.\d+)?)\s*cm\s*(?:\([^)]+\))?\s*[xX×]\s*(\d+(?:\.\d+)?)\s*cm/i);
        if (valueMatch) {
          const wVal = valueMatch[1];
          const hVal = valueMatch[2];
          const dVal = valueMatch[3];
          const wIn = (parseFloat(wVal) / 2.54).toFixed(2);
          const hIn = (parseFloat(hVal) / 2.54).toFixed(2);
          const dIn = (parseFloat(dVal) / 2.54).toFixed(2);
          expanded.push(`Width: ${wVal} cm (${wIn} in)`);
          expanded.push(`Height: ${hVal} cm (${hIn} in)`);
          expanded.push(`Depth: ${dVal} cm (${dIn} in)`);
          continue;
        }
      }

      expanded.push(m);
    }

    // Now normalize measurements that are missing colons
    // e.g., "Height 22cm" -> "Height: 22cm", "lens width 6.5cm" -> "Lens Width: 6.5cm"
    const normalized = expanded.map(m => {
      // If line has a colon, leave it alone
      if (m.includes(':')) return m;

      // Match pattern like "Height 22cm" or "Width 22cm (8.66in)" or "Total Length 99cm"
      // Also handles compound labels like "Lens Width", "Lens Height", "Frame Width", "Temple Length"
      const match = m.match(/^(Height|Width|Depth|Length|Vertical|Shoulder|Bust|Waist|Inseam|Rise|Hem|Chest|Sleeve|Handle|Strap|Outsole|Insole|Drop|Heel|Platform|Instep|Total\s+Length|Number\s+of\s+holes?|Buckle|Lens\s+Width|Lens\s+Height|Frame(?:\s+Width)?|Temple(?:\s+Length)?|Bridge)\s+(\d+.*)/i);
      if (match) {
        // Capitalize each word in the label
        const label = match[1].toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
        return label + ': ' + match[2];
      }
      return m;
    });

    // Filter out invalid/garbage lines and non-measurement fields
    const filtered = normalized.map(m => {
      // Strip "Other:" or "Others:" prefix from measurements
      m = m.replace(/^Others?\s*:\s*/i, '');
      return m;
    }).filter(m => {
      // Skip garbage lines: just dashes, brackets, or very short
      if (/^[\s\-\[\]\.]+$/.test(m) || m.length < 3) {
        return false;
      }
      // Skip lines containing product titles (long lines with brand names + product type)
      if (/^(prada|gucci|louis vuitton|chanel|hermes|dior|christian dior|fendi|balenciaga|burberry|celine|bottega|loewe|valentino|givenchy|ysl|saint laurent|versace|miu miu|coach|michael kors)/i.test(m)) {
        return false;
      }
      // Skip lines with "-Notation size" mixed in (malformed lines)
      if (/-\s*notation\s*size/i.test(m)) {
        return false;
      }
      // Skip lines that are clearly item descriptions, not measurements
      if (/\b(t-shirt|shirt|dress|jacket|pants|skirt|bag|wallet|handbag|sneaker|shoe|boot|earring)\b/i.test(m) && m.length > 50) {
        return false;
      }
      // Skip lines that look like condition descriptions
      if (/\b(used item|condition|like new|pre-owned|scratches|wear|signs of use)\b/i.test(m)) {
        return false;
      }
      // Skip non-measurement fields that shouldn't be in Size section
      if (/^(season|pockets|open\/close|closure|lining|interior|exterior)\s*:/i.test(m)) {
        return false;
      }
      // Skip lines that are just "None" values
      if (/:\s*none\s*$/i.test(m)) {
        return false;
      }
      // Skip Japanese shipping/delivery disclaimers that got into measurements
      if (/scheduled delivery|sagawa express|hikyaku courier|yamato transport|japan post|kuroneko/i.test(m)) {
        return false;
      }
      if (/customers cannot specify|delivery method|delivery company/i.test(m)) {
        return false;
      }
      if (/we will ship within|ship within \d+ business days|after payment|excluding weekends/i.test(m)) {
        return false;
      }
      if (/^\d+\s*size\s*\*?customers/i.test(m)) {
        return false;
      }
      // Skip imprint/stamp markers - these go in Description, not Size
      if (/^(imprint|stamp)\s*:\s*[A-Z0-9□]/i.test(m)) {
        return false;
      }
      // Skip pocket descriptions - these belong in Features, not Size
      if (/\b(pocket|external pocket|inner pocket|outside pocket|inside pocket)\s*:/i.test(m)) {
        return false;
      }
      if (/\d+\s*(open\s+)?pocket|\d+\s*zipper\s+pocket/i.test(m)) {
        return false;
      }
      // Skip year/production lines - these aren't measurements
      if (/^year\s*(of\s*)?(production|manufacture)/i.test(m)) {
        return false;
      }
      // Skip opening/closing type - this goes in Features
      if (/^opening\/?closing\s*(type)?\s*:/i.test(m)) {
        return false;
      }
      // Skip item number lines
      if (/item\s*number/i.test(m)) {
        return false;
      }
      // Skip material lines (contain % but are materials, not measurements)
      // e.g., "Main Item: 100% Cotton" should NOT become "Main Item: 100cm"
      if (/\d+\s*%\s*[a-z]/i.test(m)) {
        return false;
      }
      // Skip lines that are just percentages without measurement units
      if (/%\s*(cotton|wool|silk|nylon|polyester|leather|canvas|linen|rayon|viscose|cashmere|acrylic)/i.test(m)) {
        return false;
      }
      return true;
    });

    // Capitalize measurement labels (first letter of each word before the colon)
    const capitalized = filtered.map(m => {
      // Match "label:" pattern and capitalize each word in the label
      return m.replace(/^([^:]+):/i, (_, label) => {
        const capLabel = label.trim().toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
        return capLabel + ':';
      });
    });

    // DEDUPLICATE: Remove duplicate measurements by label (keep first occurrence)
    const seenLabels = new Set();
    const deduplicated = capitalized.filter(m => {
      const colonIdx = m.indexOf(':');
      if (colonIdx > 0) {
        const label = m.slice(0, colonIdx).toLowerCase().trim();
        if (seenLabels.has(label)) {
          return false; // Skip duplicate
        }
        seenLabels.add(label);
      }
      return true;
    });

    let formatted = deduplicated.join('<br>');

    // Convert half sizes: "37 1 2" -> "37.5", "EU37 1 2" -> "EU 37.5"
    formatted = formatted.replace(/(\d+)\s+1\s+2(?=\s|\(|<|$)/g, '$1.5');

    // Add space between EU/US and number: "EU37" -> "EU 37", "US7" -> "US 7"
    formatted = formatted.replace(/\b(EU|US)(\d)/gi, '$1 $2');

    // Normalize "Vertical" to "Height"
    formatted = formatted.replace(/vertical\s*:/gi, 'Height:');

    // NOTE: W:/H:/B: abbreviations are NOT expanded because:
    // - For bags: W = Width, H = Height (not Waist/Hips)
    // - For clothing: these should already be expanded in source data
    // Leaving them as W:/H: is clearer than incorrectly converting to Waist:/Hips:

    // Convert "Handlebars" to "Shoulder Strap"
    formatted = formatted.replace(/Handlebars?\s*:/gi, 'Shoulder Strap:');

    // NOTE: "Width x Height x Width (Depth):" format is now handled in the expansion loop above
    // and split into separate Width/Height/Depth measurements

    // Clean up dimension format: add spaces around "x"
    formatted = formatted.replace(/(\d+(?:\.\d+)?cm\s*\([^)]+\))x/gi, '$1 x ');
    formatted = formatted.replace(/x(\d+(?:\.\d+)?cm)/gi, ' x $1');

    // Clean up "Handle:" and "Strap:" measurements - remove leading dash
    formatted = formatted.replace(/(Handle|Strap):\s*-/gi, '$1: ');

    // Add cm/inch conversion where missing, but NOT for year/production fields
    // Split into individual lines to process each separately
    formatted = formatted.split('<br>').map(line => {
      // Skip conversion for year/production/date lines
      if (/year|production|manufacture|date|code|serial/i.test(line)) {
        return line;
      }
      // Skip conversion for Weight lines (grams, not cm)
      if (/^weight\s*:/i.test(line.trim())) {
        return line;
      }
      // Skip conversion for size label lines (e.g., "Size: 42")
      if (/^size:\s*\d+(\.\d+)?(\s*[A-Z])?$/i.test(line.trim())) {
        return line;
      }
      // First: Convert inch values to cm (e.g., "Width: 1.37"" -> "Width: 3.48cm (1.37in)")
      // Handle " or ' (inch) or in/inch suffix
      line = line.replace(/:\s*(\d+(?:\.\d+)?)\s*["'"'](?!\s*\()/gi, (match, num) => {
        const inchVal = parseFloat(num);
        const cmVal = (inchVal * 2.54).toFixed(2);
        return ': ' + cmVal + ' cm (' + inchVal.toFixed(2) + ' in)';
      });
      // Also handle "1.37in" or "1.37 in" format (without parentheses already)
      line = line.replace(/:\s*(\d+(?:\.\d+)?)\s*in\b(?!\s*\()/gi, (match, num) => {
        const inchVal = parseFloat(num);
        const cmVal = (inchVal * 2.54).toFixed(2);
        return ': ' + cmVal + ' cm (' + inchVal.toFixed(2) + ' in)';
      });
      // Apply conversion to lines with bare numbers after colon (assumes cm)
      // NOTE: Added g|kg|lb|oz to skip weight units (grams, kilograms, pounds, ounces)
      return line.replace(/:\s*(\d+(?:\.\d+)?)(?![.\d])(?!cm|in|"|mm|'|g|kg|lb|oz|\s*cm|\s*in|\s*"|\s*mm|\s*'|\s*g|\s*kg|\s*\()/gi, (match, num) => {
        const inches = (parseFloat(num) / 2.54).toFixed(2);
        return ': ' + num + ' cm (' + inches + ' in)';
      });
    }).join('<br>');

    // Remove trailing periods from measurements (e.g., "32.5cm (12.80in)." -> "32.5cm (12.80in)")
    formatted = formatted.replace(/(\d+(?:\.\d+)?(?:cm|in|mm)\s*(?:\([^)]+\))?)\./g, '$1');

    // Add space between number and unit: "3.48cm" -> "3.48 cm", "1.37in" -> "1.37 in"
    // This catches any measurements that came through from source data without spaces
    formatted = formatted.replace(/(\d+(?:\.\d+)?)(cm|in|mm)\b/gi, '$1 $2');

    // Clean up formatting
    formatted = formatted
      .replace(/(<br\s*\/?>\s*){2,}/gi, '<br>')
      .replace(/^(<br\s*\/?>)+/i, '')
      .replace(/(<br\s*\/?>)+$/i, '')
      .trim();

    return formatted;
  }

  /**
   * Extract size from product title
   */
  function extractSizeFromTitle() {
    const titleEl = document.querySelector('.product__title, .product-single__title, h1.title, [data-product-title]');
    if (!titleEl) return null;

    const title = titleEl.textContent || '';

    // Pattern 1: "SIZE XX" with letter sizes (SM, XS, S, M, L, XL, XXL, XXXL, OS)
    const sizeLetterMatch = title.match(/\bSIZE\s+(XXS|XS|SM|S|M|L|XL|XXL|XXXL|OS|ONE\s*SIZE)\b/i);
    if (sizeLetterMatch) return sizeLetterMatch[1].toUpperCase().replace(/\s+/g, ' ');

    // Pattern 2: "SIZE XX" with numeric sizes (like SIZE 42, SIZE 38.5)
    // Skip if this is a dimension pattern like "Size 30 x 235cm"
    const isDimensionPattern = /\bSIZE\s+\d+(?:\.\d+)?\s*(?:cm)?\s*x\s*\d+/i.test(title);
    if (!isDimensionPattern) {
      const sizeNumMatch = title.match(/\bSIZE\s+(\d+(?:[\s.]\d+)*(?:\s*[A-Z])?)/i);
      if (sizeNumMatch) {
        let size = sizeNumMatch[1].trim();
        // Don't return if the "letter" captured is "x" (dimension separator)
        if (/\s*x$/i.test(size)) {
          size = size.replace(/\s*x$/i, '').trim();
        }
        // Convert "42 1 2" to "42.5"
        size = size.replace(/^(\d+)\s+1\s+2(\s*[A-Z])?$/i, (m, num, letter) => {
          return num + '.5' + (letter || '');
        });
        // Don't return bare numbers from dimension patterns
        if (size && !/^\d+$/.test(size)) {
          return size;
        } else if (size) {
          // Only return bare numbers if they look like clothing sizes (30-70) and not dimensions
          const num = parseInt(size, 10);
          if (num >= 30 && num <= 60) return size;
        }
      }
    }

    // Pattern 3: Standalone letter size at end of title (e.g., "Jacket SM", "Cardigan M")
    const endLetterMatch = title.match(/\s(XXS|XS|SM|S|M|L|XL|XXL|XXXL)\s*(?:Women'?s?|Men'?s?|Unisex)?\s*$/i);
    if (endLetterMatch) return endLetterMatch[1].toUpperCase();

    // Pattern 4: Letter size before "Women's" or "Men's" (e.g., "Pink SM Women's")
    const beforeGenderMatch = title.match(/\b(XXS|XS|SM|S|M|L|XL|XXL|XXXL)\s+(?:Women'?s?|Men'?s?|Unisex)\b/i);
    if (beforeGenderMatch) return beforeGenderMatch[1].toUpperCase();

    // Pattern 5: Numeric size at end (EU/clothing sizes 30-70 to cover men's)
    const endNumMatch = title.match(/\s(\d{2})\s*$/);
    if (endNumMatch) {
      const num = parseInt(endNumMatch[1], 10);
      if (num >= 30 && num <= 70) return endNumMatch[1];
    }

    // Pattern 6: "EU XX" or "US XX" in title
    const sizeSystemMatch = title.match(/\b(EU|US|UK|IT|FR)\s*(\d+(?:\.\d+)?)\b/i);
    if (sizeSystemMatch) return sizeSystemMatch[1].toUpperCase() + ' ' + sizeSystemMatch[2];

    // Pattern 7: Size in parentheses like "(42)" or "(M)"
    const parenMatch = title.match(/\((\d{2}|XXS|XS|SM|S|M|L|XL|XXL|XXXL)\)/i);
    if (parenMatch) return parenMatch[1].toUpperCase();

    return null;
  }

  /**
   * Extract dimensions from product title (e.g., "Size 30 x 235cm" -> Width: 30cm, Length: 235cm)
   */
  function extractDimensionsFromTitle() {
    const titleEl = document.querySelector('.product__title, .product-single__title, h1.title, [data-product-title]');
    if (!titleEl) return [];

    const title = titleEl.textContent || '';
    const dimensions = [];

    // Pattern: "Size 30 x 235cm" or "30 x 235cm" or "30cm x 235cm"
    // For mufflers/scarves: first dimension is width, second is length
    const dimMatch = title.match(/\bSize\s+(\d+(?:\.\d+)?)\s*(?:cm)?\s*x\s*(\d+(?:\.\d+)?)\s*cm/i);
    if (dimMatch) {
      const width = dimMatch[1];
      const length = dimMatch[2];
      // Add width
      const widthInches = (parseFloat(width) / 2.54).toFixed(2);
      dimensions.push(`Width: ${width}cm (${widthInches}in)`);
      // Add length
      const lengthInches = (parseFloat(length) / 2.54).toFixed(2);
      dimensions.push(`Length: ${length}cm (${lengthInches}in)`);
    }

    // Also check for 3-dimensional format: "30 x 40 x 10cm"
    const dim3Match = title.match(/(\d+(?:\.\d+)?)\s*(?:cm)?\s*x\s*(\d+(?:\.\d+)?)\s*(?:cm)?\s*x\s*(\d+(?:\.\d+)?)\s*cm/i);
    if (dim3Match && dimensions.length === 0) {
      const h = dim3Match[1];
      const w = dim3Match[2];
      const d = dim3Match[3];
      dimensions.push(`Height: ${h}cm (${(parseFloat(h) / 2.54).toFixed(2)}in)`);
      dimensions.push(`Width: ${w}cm (${(parseFloat(w) / 2.54).toFixed(2)}in)`);
      dimensions.push(`Depth: ${d}cm (${(parseFloat(d) / 2.54).toFixed(2)}in)`);
    }

    return dimensions;
  }

  /**
   * Build the formatted description HTML
   */
  function buildFormattedDescription(data) {
    const sections = [];

    // NOTE: Condition is now its own separate accordion tab, so we don't include it here

    // Keys to skip in description output (condition-related fields belong in Condition tab)
    const keysToSkip = ['Rank', 'Item Rank', 'Engraving', 'Engraved',
                        'Product Number', 'Management Number', 'Control Number',
                        'Notation Size', 'Reference Size', 'Size Indicated', 'Notes',
                        'Reference Price', 'Product Name', 'Condition Rank', 'Remarks',
                        'Shape Number'];

    // Pattern to detect condition-related values
    // Note: "mark" removed - too generic, causes false positives with product names like "Coco Mark"
    const conditionValuePattern = /\b(scratch|stain|wear|dirt|fad|crack|tear|rub|peel|tarnish|oxidat|damage|dent|chip|spot|discolor|fraying|peeling|scuff)\b/i;

    // Fields that are product identifiers - never filter these by condition keywords
    const productIdentifierFields = ['Model', 'Model Name', 'Series', 'Line', 'Brand', 'Category'];

    // Details section - ordered: Brand, Category, Material, Color, Series, Features, then others
    // Accessories is handled separately at the end (after the description paragraph)
    // Note: Shape Number removed - internal product codes aren't useful to customers
    const detailKeys = ['Brand', 'Category', 'Color', 'Hardware Color', 'Series', 'Model', 'Model Name', 'Features', 'Pockets', 'Opening', 'Opening/Closing Type', 'Imprint', 'Year Of Production', 'Serial', 'Date Code', 'Weight', 'Stone', 'Hardware', 'Marking'];
    const detailLines = [];
    for (const key of detailKeys) {
      if (data.details[key] && !keysToSkip.includes(key)) {
        // Clean the value - skip if it's empty after cleaning (e.g., only "as shown in image")
        const cleanedValue = cleanDisplayValue(data.details[key]);
        if (!cleanedValue) continue;
        // Skip condition check for product identifier fields (Model Name, Series, etc.)
        // These often contain words like "Mark" that aren't condition-related
        if (!productIdentifierFields.includes(key) && conditionValuePattern.test(cleanedValue)) {
          data.condition.push(key + ': ' + cleanedValue);
          continue;
        }
        detailLines.push(`<strong>${key}:</strong> ${cleanedValue}`);
      }
    }
    // Add any other details not in the standard list (but skip unwanted fields and Accessories)
    for (const [key, value] of Object.entries(data.details)) {
      if (!detailKeys.includes(key) && key !== 'Accessories' && value && !keysToSkip.includes(key)) {
        // Clean the value - skip if it's empty after cleaning
        const cleanedValue = cleanDisplayValue(value);
        if (!cleanedValue) continue;
        // Also skip any key that looks like a measurement (should be in size section)
        if (isMeasurementLine(key + ': ' + cleanedValue)) {
          data.measurements.push(key + ': ' + cleanedValue);
          continue;
        }
        // Skip if value contains condition keywords (should be in Condition tab)
        if (conditionValuePattern.test(cleanedValue)) {
          data.condition.push(key + ': ' + cleanedValue);
          continue;
        }
        detailLines.push(`<strong>${key}:</strong> ${cleanedValue}`);
      }
    }
    // Add Material inline (uniform format)
    if (data.material && data.material !== '-') {
      detailLines.push(`<strong>Material:</strong> ${data.material}`);
    }

    // Add Country of Origin inline (uniform format)
    // Skip useless placeholder values like "Notation: None", "None", etc.
    if (data.countryOfOrigin &&
        !/^notation\s*:\s*none$/i.test(data.countryOfOrigin.trim()) &&
        !/^none$/i.test(data.countryOfOrigin.trim()) &&
        !/^n\/a$/i.test(data.countryOfOrigin.trim()) &&
        !/^-+$/.test(data.countryOfOrigin.trim())) {
      detailLines.push(`<strong>Country of Origin:</strong> ${data.countryOfOrigin}`);
    }

    if (detailLines.length > 0) {
      sections.push('<div class="reluxe-desc-section">');
      sections.push('<div class="reluxe-desc-content">' + detailLines.join('<br>') + '</div>');
      sections.push('</div>');
    }

    // Authenticity
    if (data.authenticity) {
      sections.push('<div class="reluxe-desc-section">');
      sections.push('<div class="reluxe-desc-content"><strong>Authenticity:</strong> ' + data.authenticity + '</div>');
      sections.push('</div>');
    }

    // Free-form description (paragraph text) - comes BEFORE Accessories
    // Filter out freeform description fields that should be hidden when using AI descriptions
    const filteredDesc = data.description.filter(line => {
      // Skip lines that start with seller description keywords
      if (/^(details?|comments?|about\s+us|description)\s*[：:\-;]/i.test(line)) return false;
      // Skip lines that are mostly about the store/seller
      if (/^our\s+store\s+is\s+/i.test(line)) return false;
      if (/flimsy\s+high\s+class/i.test(line)) return false;
      // Skip generic seller commentary about usability (redundant with AI descriptions)
      if (/can\s+still\s+be\s+used/i.test(line)) return false;
      if (/^(but|however)\.?\s*(it\s+)?(is\s+)?(still\s+)?(usable|functional|works?)/i.test(line)) return false;
      return true;
    });
    if (filteredDesc.length > 0) {
      sections.push('<div class="reluxe-desc-freeform">' + filteredDesc.join('<br><br>') + '</div>');
    }

    // Accessories - shown at the end, after the description paragraph
    const cleanedAccessories = cleanDisplayValue(data.details.Accessories);
    if (cleanedAccessories) {
      sections.push('<div class="reluxe-desc-section">');
      sections.push('<div class="reluxe-desc-content"><strong>Accessories:</strong> ' + cleanedAccessories + '</div>');
      sections.push('</div>');
    }

    return sections.join('\n');
  }

  /**
   * Check if this is a ring product (jewelry ring)
   * Checks product type and title - avoids false positives like "Ring Handle Bag"
   */
  function isRingProduct() {
    // Check product type/category from various sources
    let productType = '';
    const typeEl = document.querySelector('[data-product-type]');
    if (typeEl) {
      productType = (typeEl.textContent || typeEl.dataset.productType || '').toLowerCase().trim();
    }
    // Also check meta tags
    if (!productType) {
      const metaType = document.querySelector('meta[property="product:category"]');
      if (metaType) {
        productType = metaType.content.toLowerCase().trim();
      }
    }

    // Check if product type is explicitly "ring" or "rings"
    if (/^rings?$/i.test(productType)) {
      return true;
    }

    // Also check the product title for ring keywords (but exclude false positives)
    const titleEl = document.querySelector('.product__title, .product-single__title, h1.title, [data-product-title]');
    if (titleEl) {
      const title = (titleEl.textContent || '').toLowerCase();
      // Must contain "ring" but NOT "ring handle", "ring bag", "key ring", "keyring"
      if (/\bring\b/i.test(title) &&
          !/ring\s*(handle|bag|strap|chain)/i.test(title) &&
          !/key\s*ring/i.test(title)) {
        return true;
      }
    }

    return false;
  }

  // Mirrors the allowed_size_types pipe-list in main-product.liquid. Every PT in
  // this set is non-returnable (apparel + shoes + rings). Update both places in sync.
  const FINAL_SALE_TYPES_LC = new Set([
    'jacket','t-shirt','sweater','pants','shirt','dress','skirt','coat','cardigan',
    'top','hoodie','vest','shorts','blouse','suit','jeans','sets','capes & ponchos',
    'sweatshirt','polo','swimwear','overalls','romper','jumpsuit','earmuffs','leg warmers',
    'sneakers','boots','sandals','loafers','heels','flats','oxfords','mules',
    'espadrilles','slippers','platforms',
    'ring'
  ]);

  function isFinalSaleProduct() {
    let pt = '';
    // PDP: Shopify exposes product type via ShopifyAnalytics.meta.product.type
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta &&
        window.ShopifyAnalytics.meta.product && window.ShopifyAnalytics.meta.product.type) {
      pt = String(window.ShopifyAnalytics.meta.product.type).toLowerCase().trim();
    }
    // Fallback: data-product-type attribute (rare on PDPs)
    if (!pt) {
      const el = document.querySelector('[data-product-type]');
      if (el) pt = (el.textContent || el.dataset.productType || '').toLowerCase().trim();
    }
    // Fallback: meta tag
    if (!pt) {
      const metaType = document.querySelector('meta[property="product:category"]');
      if (metaType) pt = (metaType.content || '').toLowerCase().trim();
    }
    if (pt && FINAL_SALE_TYPES_LC.has(pt)) return true;
    return isRingProduct();
  }

  /**
   * Check if a line is condition content (not a measurement)
   */
  function isConditionContent(line) {
    // Skip disclaimer text - don't flag as condition just because of "slight"
    if (/please\s+allow\s+(for\s+)?slight\s+variation/i.test(line)) {
      return false;
    }

    // FIRST: Check if this is a measurement line
    // If it's a measurement, don't flag it as condition even if it contains soft keywords
    if (isMeasurementLine(line)) {
      // Only flag measurement lines as condition if they contain STRONG condition keywords
      const strongConditionKeywords = /\b(scuff|scratch|stain|dirt|fad|crack|tear|rub|peel|tarnish|oxidat|damage|dent|chip|spot|discolor|fraying|peeling|broken|worn)\b/i;
      return strongConditionKeywords.test(line);
    }

    // For non-measurement lines, check for any condition keywords
    const conditionKeywords = /\b(scuff|scratch|stain|wear|dirt|mark|fad|crack|tear|rub|peel|tarnish|oxidat|damage|dent|chip|spot|discolor|fraying|peeling|self.?repair|odor|broken|worn|noticeable|visible|minor|slight|light|faint|rubbing|crease|corner wear|no tears|color fad|plating|edge.?crack|cuts)\b/i;

    // Check if line contains condition keywords
    if (conditionKeywords.test(line)) {
      return true;
    }

    // Lines starting with condition-related location markers
    if (/^(surface|corner|handle|bottom|edge|strap|clasp|hardware|zipper|lining|interior|exterior|inside|outside|side|metal\s*parts?|main\s*(mouth|opening)|opening\/closing\s*opening)\s*:/i.test(line)) {
      // But only if they don't contain actual measurements
      if (!/\d+(?:\.\d+)?\s*(?:cm|in|mm)/i.test(line)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Build size tab content
   */
  function buildSizeContent(data) {
    let content = '';

    // Size label
    let sizeLabel = data.sizeLabel;
    if (!sizeLabel) {
      sizeLabel = extractSizeFromTitle();
    }

    // Filter out useless sizeLabel values like "Notation None", "None", "Please refer...", bullets only, etc.
    if (sizeLabel && (/^none/i.test(sizeLabel) || /please\s+refer/i.test(sizeLabel) ||
        /^notation\s*none/i.test(sizeLabel) || /^-+$/.test(sizeLabel) || /^n\/a$/i.test(sizeLabel) ||
        /\(please\s+refer/i.test(sizeLabel) || /^[•·\-\s]+$/.test(sizeLabel))) {
      sizeLabel = null;
    }

    // Remove "Issue" prefix from size (e.g., "Issue 14.5(54)" → "14.5(54)")
    if (sizeLabel) {
      sizeLabel = sizeLabel.replace(/^issue\s+/i, '').trim();
      // Add space between number and parenthesis (e.g., "14.5(54)" → "14.5 (54)")
      sizeLabel = sizeLabel.replace(/(\d)(\()/g, '$1 $2');
    }

    // Try to extract dimensions from title (e.g., "Size 30 x 235cm") and add to measurements
    const titleDimensions = extractDimensionsFromTitle();
    if (titleDimensions.length > 0) {
      for (const dim of titleDimensions) {
        if (!data.measurements.some(m => m.toLowerCase().includes(dim.toLowerCase().split(':')[0]))) {
          data.measurements.push(dim);
        }
      }
    }

    // Separate condition content from measurements
    const realMeasurements = [];
    for (const m of data.measurements) {
      if (isConditionContent(m)) {
        // Move to condition array
        data.condition.push(m);
      } else {
        realMeasurements.push(m);
      }
    }

    // Build measurements box
    let measurementBoxContent = '';

    if (sizeLabel) {
      measurementBoxContent += `Size: ${sizeLabel}`;
    }

    if (realMeasurements.length > 0) {
      const formattedMeasurements = formatMeasurements(realMeasurements);
      if (formattedMeasurements) {
        if (measurementBoxContent) measurementBoxContent += '<br>';
        measurementBoxContent += formattedMeasurements;
      }
    }

    if (measurementBoxContent) {
      content = `<div class="reluxe-accordion__measurements">${measurementBoxContent}</div>`;
    } else {
      content = '<p>One Size</p><p style="color: #666; font-size: 13px;">No specific measurements available for this item.</p>';
    }

    // Add ring size chart note if applicable (check notes OR if it's a ring product)
    const hasRingSizeNote = data.notes.some(n => /ring size/i.test(n));
    if (hasRingSizeNote || isRingProduct()) {
      content += '<p style="margin-top: 12px;"><em style="font-size: 11px; color: #888;">Ring size listed is international sizing. <a href="#" onclick="ReluxeAccordion.showRingSizeChart(); return false;" style="color: #888; text-decoration: underline;">View Ring Size Chart</a></em></p>';
    }

    return content;
  }

  /**
   * Build condition tab content
   */
  function buildConditionContent(data) {
    if (!data.condition || data.condition.length === 0) {
      return '<p>No additional condition notes for this item.</p>';
    }

    // Filter and clean condition entries
    const filtered = data.condition
      .map(c => cleanConditionText(c))  // Clean each entry
      .filter(c => {
        // Skip entries that are just bracket headers like "[Outside]." or "[Inside]."
        if (/^\[[\w\s]+\]\.?$/.test(c.trim())) {
          return false;
        }
        // Skip entries that are just dashes/punctuation
        if (/^[\s\-\.\[\]]+$/.test(c)) {
          return false;
        }
        // Skip very short entries (less than 3 chars)
        if (c.trim().length < 3) {
          return false;
        }
        // Skip section headers with no content (e.g., "Damage details", "Item details")
        if (/^\s*(damage\s+details?|item\s+details?|size\s+details?|condition\s+details?)\s*[:\.]?\s*$/i.test(c.trim())) {
          return false;
        }
        // Skip disclaimer text about measurements
        if (/please\s+allow\s+(for\s+)?slight\s+variation/i.test(c)) {
          return false;
        }
        // Skip useless/generic condition statements
        if (isUselessCondition(c)) {
          return false;
        }
        // Skip rank entries (we don't use these)
        if (/^rank\s*:\s*/i.test(c)) {
          return false;
        }
        // Skip detail fields that don't belong in Condition tab
        // These are product details that got misclassified
        if (/^(model\s*name|model|brand|color|colour|material|category|series|line|type|style|size|accessories|features|hardware|year|date\s*code|serial|imprint|country|weight|stone|pockets|closure|opening)\s*:/i.test(c.trim())) {
          return false;
        }
        // Skip short generic condition statements (3 words or less)
        // e.g., "Good", "Very Good", "Excellent Condition" - not informative
        const wordCount = c.trim().split(/\s+/).length;
        if (wordCount <= 3) {
          // Check if it's just a generic quality rating
          if (/^(good|very good|excellent|fair|poor|like new|new|mint|great|nice|beautiful|perfect|pristine|immaculate|unused|gently used|lightly used|heavily used|well used|condition\s*:?\s*(good|excellent|fair|poor|new|mint)?)$/i.test(c.trim())) {
            return false;
          }
        }
        return true;
      });

    if (filtered.length === 0) {
      return '<p>No additional condition notes for this item.</p>';
    }

    return `<div class="reluxe-accordion__condition">${filtered.join('<br>')}</div>`;
  }

  /**
   * Build accordion HTML
   */
  function buildAccordion(descriptionEl) {
    const originalHTML = descriptionEl.innerHTML;

    // Separate non-description elements
    const { cleanHTML, extractedElements } = separateNonDescriptionElements(originalHTML);

    // Parse the description
    let data = parseDescription(cleanHTML);

    // If no data (empty description), create empty data structure so accordion still builds
    if (!data) {
      data = {
        condition: [],
        details: {},
        material: null,
        countryOfOrigin: null,
        measurements: [],
        sizeLabel: null,
        authenticity: null,
        description: [],
        notes: []
      };
    }

    // Build formatted description
    const descriptionContent = buildFormattedDescription(data);

    // Build size content
    const sizeContent = buildSizeContent(data);

    // Build condition content
    const conditionContent = buildConditionContent(data);

    // Create accordion structure
    const accordionHTML = `
      <div class="reluxe-accordion">
        <div class="reluxe-accordion__item reluxe-accordion__item--open">
          <button class="reluxe-accordion__header" aria-expanded="true">
            <span>Description</span>
            <svg class="reluxe-accordion__icon" viewBox="0 0 24 24" width="20" height="20">
              <path d="M7 10l5 5 5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
          <div class="reluxe-accordion__content" style="display: block;">
            <div class="reluxe-accordion__body">
              ${descriptionContent}
            </div>
          </div>
        </div>

        <div class="reluxe-accordion__item">
          <button class="reluxe-accordion__header" aria-expanded="false">
            <span>Size & Measurements</span>
            <svg class="reluxe-accordion__icon" viewBox="0 0 24 24" width="20" height="20">
              <path d="M7 10l5 5 5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
          <div class="reluxe-accordion__content" style="display: none;">
            <div class="reluxe-accordion__body">
              ${sizeContent}
            </div>
          </div>
        </div>

        <div class="reluxe-accordion__item">
          <button class="reluxe-accordion__header" aria-expanded="false">
            <span>Condition</span>
            <svg class="reluxe-accordion__icon" viewBox="0 0 24 24" width="20" height="20">
              <path d="M7 10l5 5 5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
          <div class="reluxe-accordion__content" style="display: none;">
            <div class="reluxe-accordion__body">
              ${conditionContent}
            </div>
          </div>
        </div>

        <div class="reluxe-accordion__item">
          <button class="reluxe-accordion__header" aria-expanded="false">
            <span>Shipping & Returns</span>
            <svg class="reluxe-accordion__icon" viewBox="0 0 24 24" width="20" height="20">
              <path d="M7 10l5 5 5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
          <div class="reluxe-accordion__content" style="display: none;">
            <div class="reluxe-accordion__body">
              ${SHIPPING_RETURNS_CONTENT}
            </div>
          </div>
        </div>
      </div>
      ${extractedElements.join('\n')}
    `;

    return accordionHTML;
  }

  /**
   * Toggle accordion item
   */
  function toggleAccordion(header) {
    const item = header.closest('.reluxe-accordion__item');
    const content = item.querySelector('.reluxe-accordion__content');
    const isOpen = item.classList.contains('reluxe-accordion__item--open');

    if (isOpen) {
      item.classList.remove('reluxe-accordion__item--open');
      header.setAttribute('aria-expanded', 'false');
      content.style.display = 'none';
    } else {
      item.classList.add('reluxe-accordion__item--open');
      header.setAttribute('aria-expanded', 'true');
      content.style.display = 'block';
    }
  }

  /**
   * Show ring size chart modal
   */
  function showRingSizeChart() {
    const existingModal = document.querySelector('.reluxe-ringsize-modal');
    if (existingModal) existingModal.remove();

    const modal = document.createElement('div');
    modal.className = 'reluxe-ringsize-modal';
    modal.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;z-index:99999;display:flex;align-items:center;justify-content:center;';
    modal.innerHTML = `
      <div class="reluxe-ringsize-modal__backdrop" style="position:absolute;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.6);backdrop-filter:blur(8px);cursor:pointer;"></div>
      <div class="reluxe-ringsize-modal__content" style="position:relative;background:#fff;padding:32px;max-width:560px;width:94%;max-height:80vh;overflow-y:auto;border-radius:8px;box-shadow:0 20px 60px rgba(0,0,0,0.3);">
        <button class="reluxe-ringsize-modal__close" aria-label="Close" style="position:absolute;top:12px;right:12px;width:32px;height:32px;border:none;background:none;cursor:pointer;padding:0;display:flex;align-items:center;justify-content:center;border-radius:50%;color:#666;">
          <svg viewBox="0 0 24 24" width="24" height="24"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor"/></svg>
        </button>
        <h3 style="margin:0 0 8px;font-size:20px;font-weight:600;color:#111;">Ring Size Conversion Chart</h3>
        <p style="font-size:13px;color:#666;margin-bottom:16px;">Find your size using the chart below.</p>
        <div style="overflow-x:auto;">
          <table style="width:100%;border-collapse:collapse;font-size:13px;">
            <thead>
              <tr style="background:#f8f8f8;">
                <th style="padding:10px 18px;text-align:center;border-bottom:1px solid #e5e5e5;font-weight:600;font-size:11px;text-transform:uppercase;">International</th>
                <th style="padding:10px 18px;text-align:center;border-bottom:1px solid #e5e5e5;font-weight:600;font-size:11px;text-transform:uppercase;">EU</th>
                <th style="padding:10px 18px;text-align:center;border-bottom:1px solid #e5e5e5;font-weight:600;font-size:11px;text-transform:uppercase;">US</th>
                <th style="padding:10px 18px;text-align:center;border-bottom:1px solid #e5e5e5;font-weight:600;font-size:11px;text-transform:uppercase;">Diameter</th>
              </tr>
            </thead>
            <tbody>
              ${[
                [4,44,3,'14.0'],[5,45,3.5,'14.4'],[6,46,3.75,'14.8'],[7,47,4,'15.0'],[8,48,4.5,'15.2'],
                [9,49,5,'15.6'],[10,50,5.25,'15.9'],[11,51,5.75,'16.1'],[12,52,6,'16.5'],[13,53,6.5,'16.9'],
                [14,54,7,'17.3'],[15,55,7.5,'17.7'],[16,56,7.75,'17.8'],[17,57,8,'18.1'],[18,58,8.5,'18.5'],
                [19,59,8.75,'18.9'],[20,60,9,'19.0'],[21,61,9.5,'19.4'],[22,62,10,'19.8'],[23,63,10.5,'20.2'],
                [24,64,11,'20.6'],[25,65,11.5,'21.0'],[26,66,12,'21.4'],[27,67,12.5,'21.8'],[28,68,13,'22.2']
              ].map(([intl,eu,us,dia]) => `<tr><td style="padding:8px 12px;text-align:center;border-bottom:1px solid #e5e5e5;">${intl}</td><td style="padding:8px 12px;text-align:center;border-bottom:1px solid #e5e5e5;">${eu}</td><td style="padding:8px 12px;text-align:center;border-bottom:1px solid #e5e5e5;">${us}</td><td style="padding:8px 12px;text-align:center;border-bottom:1px solid #e5e5e5;">${dia}</td></tr>`).join('')}
            </tbody>
          </table>
        </div>
      </div>
    `;

    document.body.appendChild(modal);
    document.body.classList.add('reluxe-modal-open');

    const closeBtn = modal.querySelector('.reluxe-ringsize-modal__close');
    const backdrop = modal.querySelector('.reluxe-ringsize-modal__backdrop');

    function closeModal() {
      modal.remove();
      document.body.classList.remove('reluxe-modal-open');
      document.removeEventListener('keydown', handleEsc);
    }

    function handleEsc(e) {
      if (e.key === 'Escape') closeModal();
    }

    closeBtn.addEventListener('click', closeModal);
    backdrop.addEventListener('click', closeModal);
    document.addEventListener('keydown', handleEsc);
  }

  /**
   * Get AI description from Shopify metafield
   */
  function getAIDescription() {
    // Try from a dedicated data element (set by Liquid template)
    const aiDescEl = document.querySelector('[data-ai-description]');
    if (aiDescEl) {
      return aiDescEl.textContent || aiDescEl.dataset.aiDescription;
    }
    return null;
  }

  /**
   * Build AI description HTML block
   */
  function buildAIDescriptionBlock(aiDescription) {
    if (!aiDescription) return '';
    return `
      <div class="reluxe-ai-description">
        ${aiDescription}
      </div>
    `;
  }

  /**
   * Initialize accordion
   */
  function init() {
    const descriptionSelectors = [
      '.product__description',
      '.product-single__description',
      '[data-product-description]',
      '.product__info-content .rte'
    ];

    let descriptionEl = null;
    for (const selector of descriptionSelectors) {
      descriptionEl = document.querySelector(selector);
      if (descriptionEl) break;
    }

    if (!descriptionEl) {
      console.log('[ReluxeAccordion] No description element found');
      return;
    }

    if (descriptionEl.querySelector('.reluxe-accordion') || descriptionEl.classList.contains('reluxe-accordion-initialized')) {
      return;
    }

    descriptionEl.classList.add('reluxe-accordion-initialized');

    // Extract AI description FIRST (before parsing) and remove from DOM
    const aiDescEl = descriptionEl.querySelector('[data-ai-description]');
    let aiDescription = null;
    if (aiDescEl) {
      aiDescription = aiDescEl.textContent || aiDescEl.dataset.aiDescription;
      aiDescEl.remove(); // Remove so it doesn't get parsed into the accordion
    }

    const accordionHTML = buildAccordion(descriptionEl);
    descriptionEl.innerHTML = accordionHTML;

    // Clean up Description tab for ALL items
    // Show AI description (if exists) + keep only important structured fields
    const descBody = descriptionEl.querySelector('.reluxe-accordion__item:first-child .reluxe-accordion__body');
    if (descBody) {
      // STRICT: Only keep these fields, hide EVERYTHING else
      const keepFields = ['material', 'series', 'model', 'model name', 'model type', 'features', 'country of origin', 'accessories', 'color', 'line', 'model line', 'weight'];
      // Useless lines to always filter out
      const uselessPatterns = [
        /^none$/i,
        /^-$/,
        /^n\/a$/i,
        /^condition\s*(level)?$/i,
        /^(gucci|louis vuitton|chanel|hermes|prada|dior|fendi|celine|bottega veneta|balenciaga|saint laurent|burberry|loewe|valentino|givenchy|cartier|tiffany|goyard)$/i,
        /^\s*$/,
        /please\s+note/i,
        /slight\s+error/i,
        /may\s+(be|have)\s+(slight|minor)/i,
      ];

      let keptLines = [];

      // Process each section
      const sections = descBody.querySelectorAll('.reluxe-desc-section');
      sections.forEach(section => {
        const label = section.querySelector('.reluxe-desc-label');
        const content = section.querySelector('.reluxe-desc-content');

        // Standalone labeled sections (Material, Country of Origin, etc.)
        if (label) {
          const labelText = label.textContent.toLowerCase().trim();
          if (keepFields.some(f => labelText.includes(f))) {
            // Keep the whole section
            keptLines.push(section.outerHTML);
            return;
          }
        }

        // Details section with multiple field entries
        if (content) {
          const html = content.innerHTML;
          const lines = html.split(/<br\s*\/?>/gi);
          const filteredLines = [];

          for (let i = 0; i < lines.length; i++) {
            const trimmed = lines[i].trim();
            if (!trimmed) continue;

            const textOnly = trimmed.replace(/<[^>]+>/g, '').trim();
            if (uselessPatterns.some(p => p.test(textOnly))) continue;

            // Check <strong>Field:</strong> value format
            const fieldMatch = trimmed.match(/<strong>([^<:]+):?<\/strong>\s*(.*)/i);
            if (fieldMatch) {
              const fieldName = fieldMatch[1].toLowerCase().trim();
              let fieldValue = fieldMatch[2].replace(/<[^>]+>/g, '').trim();

              // If no value on same line, check next line
              if (!fieldValue && i + 1 < lines.length) {
                const nextLine = lines[i + 1].replace(/<[^>]+>/g, '').trim();
                // Only use next line if it doesn't look like another label
                if (nextLine && !nextLine.match(/^[A-Z][a-z]+:?\s*$/) && !uselessPatterns.some(p => p.test(nextLine))) {
                  fieldValue = nextLine;
                  i++; // Skip next line since we used it as value
                }
              }

              if (uselessPatterns.some(p => p.test(fieldValue))) continue;

              const shouldKeep = keepFields.some(f => fieldName.includes(f) || f.includes(fieldName));

              if (shouldKeep && fieldValue) {
                // Uniform format: Field: Value
                filteredLines.push(`<strong>${fieldMatch[1].trim()}:</strong> ${fieldValue}`);
              }
            } else {
              // Check for plain "Label" on one line, value on next (no strong tags)
              const plainLabel = textOnly.match(/^([A-Z][a-z]+(?:\s+[A-Za-z]+)?)$/);
              if (plainLabel && i + 1 < lines.length) {
                const labelName = plainLabel[1].toLowerCase().trim();
                const nextLine = lines[i + 1].replace(/<[^>]+>/g, '').trim();

                if (nextLine && !uselessPatterns.some(p => p.test(nextLine))) {
                  const shouldKeep = keepFields.some(f => labelName.includes(f) || f.includes(labelName));
                  if (shouldKeep) {
                    filteredLines.push(`<strong>${plainLabel[1]}:</strong> ${nextLine}`);
                    i++; // Skip next line
                  }
                }
              }

              // Preserve important standalone authentication/quality notes
              if (/no\s+(?:carat|karat)\s+marking'?s?/i.test(textOnly)) {
                filteredLines.push('<strong>Note:</strong> No Carat Marking');
              }
            }
          }

          if (filteredLines.length > 0) {
            keptLines.push('<div class="reluxe-desc-section"><div class="reluxe-desc-content">' + filteredLines.join('<br>') + '</div></div>');
          }
        }
      });

      // Remove ALL freeform content - strict KEEP ONLY approach
      const freeform = descBody.querySelector('.reluxe-desc-freeform');
      if (freeform) {
        freeform.remove();
      }

      // Build clean Description tab
      let cleanHTML = '';

      // Add AI description at top if it exists
      if (aiDescription) {
        cleanHTML += `
          <div class="reluxe-ai-description" style="margin-bottom: 20px; line-height: 1.6;">
            ${aiDescription}
          </div>
        `;
      }

      // Add kept fields
      if (keptLines.length > 0) {
        cleanHTML += keptLines.join('');
      }

      // If nothing to show and no AI description, show a minimal message
      if (!cleanHTML) {
        cleanHTML = '<p style="color: #666;">See product images for details.</p>';
      }

      // Replace content with clean version
      descBody.innerHTML = cleanHTML;
    }

    const headers = descriptionEl.querySelectorAll('.reluxe-accordion__header');
    headers.forEach(header => {
      header.addEventListener('click', () => toggleAccordion(header));
    });

    if (isFinalSaleProduct() && !document.querySelector('.reluxe-final-sale-disclaimer')) {
      const notAffiliatedEl = Array.from(document.querySelectorAll('p.product__text.inline-richtext'))
        .find(p => /not\s+affiliated/i.test(p.textContent));
      const anchor = notAffiliatedEl || descriptionEl;
      const disclaimer = document.createElement('p');
      disclaimer.className = 'reluxe-final-sale-disclaimer';
      disclaimer.setAttribute('style', 'margin-top: 1em; font-size: 1rem; color: #888; text-align: center; font-style: italic;');
      disclaimer.textContent = 'This item is final sale and not eligible for return.';
      if (notAffiliatedEl) {
        anchor.insertAdjacentElement('afterend', disclaimer);
      } else {
        descriptionEl.appendChild(disclaimer);
      }
    }

    console.log('[ReluxeAccordion] Initialized');
  }

  function initIfProductPage()  {
    if (window.location.pathname.includes('/products/')) {
      init();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initIfProductPage);
  } else {
    initIfProductPage();
  }

  window.ReluxeAccordion = { init, showRingSizeChart };

})();
